import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  writeBatch,
  query,
  orderBy,
  documentId,
  startAt,
  endAt,
  Timestamp,
  serverTimestamp,
  deleteField
} from 'firebase/firestore';
import { db } from '../firebase/config';

// 관련 학생 정보 타입 정의
interface RelatedStudentInfo {
  id: string;
  name: string;
  number: string;
  groupName?: string;
}

export interface NotebookNote {
  id: string;
  content: string;
  categoryId?: string; // ✅ 노트에 저장하지 않으므로 optional (키에서 동적 할당)
  relatedStudents?: RelatedStudentInfo[];
  date: string;
  timeTagId?: string;
  timeTagName?: string;
  fontSize?: number; // UI에서 localStorage로 관리하므로 optional
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface CategoryCache {
  notes: NotebookNote[];
  lastUpdated: Timestamp;
  categoryId: string;
}

export interface NotebookCategory {
  id: string;
  name: string;
  order: number;
  createdAt: Timestamp;
  /** 잠금 여부. true면 해당 카테고리 클릭 시 노트 비밀번호 입력 후 열림 */
  locked?: boolean;
  /** 잠금 시 사용하는 비밀번호의 SHA-256 해시 (클라이언트에서 입력값과 비교용) */
  lockPasswordHash?: string;
}

export interface NoticeNoteEntry {
  content: string;
  fontSize?: number; // UI에서 localStorage로 관리하므로 optional
  date: string; // 날짜 필드 추가
  createdAt: Timestamp;
  updatedAt: Timestamp;
  // NOTE: 저장은 GeneralNoticeAll/GeneralNoticeGroup 필드에 분리 저장하며,
  // 조회 시 scope가 주입됩니다(레거시 GeneralNotice는 scope='all'로 취급).
  scope?: 'all' | 'group';
}

export type NoticeNoteField = 'GeneralNotice' | 'IndividualNotice';

export interface IndividualNoticeStudentBucket {
  studentId: string;
  studentName?: string;
  notes: NoticeNoteEntry[];
}

export type NoticeNotesDoc = {
  // legacy
  GeneralNotice?: NoticeNoteEntry[];
  // new: 전체공지/그룹공지 분리
  GeneralNoticeAll?: NoticeNoteEntry[];
  GeneralNoticeGroup?: NoticeNoteEntry[];
  // 학생별 개별공지 저장
  IndividualNotice: IndividualNoticeStudentBucket[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
};

// ===== V2: 중첩 객체 구조 (배열 → 객체 전환 시) =====
// 현재: GeneralNoticeAll, GeneralNoticeGroup, IndividualNotice 모두 배열 → 한 항목 수정/삭제 시 전체 배열을 다시 써야 함.
// V2 객체로 저장 시:
//   - GeneralNoticeAll/Group: { [entryKey]: NoticeNoteEntry } → updateDoc({ [`GeneralNoticeAll.${key}`]: entry }) 또는 deleteField
//   - IndividualNotice: { [studentId]: { studentName?, notes: { [noteKey]: NoticeNoteEntry } } } → 한 학생/한 노트만 부분 업데이트 가능
// 읽기: getNoticeNotes에서 배열/객체 둘 다 처리(듀얼 읽기). 저장은 당분간 기존 배열 유지 후, 원하면 저장 경로를 V2로 전환 가능.

/** 공지 엔트리 키 (createdAt 기준 고유 키) */
export function getNoticeEntryKey(entry: { createdAt: Timestamp }): string {
  if (!entry?.createdAt) return `_${Date.now()}`;
  const t = entry.createdAt as Timestamp;
  const ms = typeof t.toMillis === 'function' ? t.toMillis() : (t as any).seconds * 1000;
  const ns = (t as any).nanoseconds ?? 0;
  return ns ? `${ms}_${ns}` : `${ms}`;
}

/** GeneralNoticeAll / GeneralNoticeGroup V2: { [entryKey]: NoticeNoteEntry } */
export type GeneralNoticeEntriesV2 = Record<string, NoticeNoteEntry>;

/** IndividualNotice V2: { [studentId]: { studentName?, notes: { [noteKey]: NoticeNoteEntry } } } */
export interface IndividualNoticeNotesV2 {
  studentName?: string;
  notes: Record<string, NoticeNoteEntry>;
}

export type IndividualNoticeV2 = Record<string, IndividualNoticeNotesV2>;

export type NoticeNotesDocV2 = {
  GeneralNoticeAll?: GeneralNoticeEntriesV2;
  GeneralNoticeGroup?: GeneralNoticeEntriesV2;
  IndividualNotice?: IndividualNoticeV2;
  createdAt: Timestamp;
  updatedAt: Timestamp;
};

export type NoticeNotesMemberDoc = {
  memberId: string;
  memberGroup?: string;
  generalNoticeAll?: NoticeNoteEntry[];
  generalNoticeGroup?: NoticeNoteEntry[];
  individualNotices?: NoticeNoteEntry[];
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
};

/** NoticeNotesMembers V2: NoticeNote-1과 동일하게 객체로 저장 (generalNoticeAll, individualNotices) */
export type NoticeNotesMemberDocV2 = {
  memberId: string;
  memberGroup?: string;
  generalNoticeAll?: GeneralNoticeEntriesV2;
  generalNoticeGroup?: GeneralNoticeEntriesV2;
  individualNotices?: Record<string, NoticeNoteEntry>;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
};

// Firestore 문서 최대 크기: 1MB (1,048,576 bytes)
// 안전하게 950KB 정도 되면 새 문서 생성
const MAX_NOTE_DOC_SIZE = 950 * 1024; // 950KB (약 1MB 전에 새 문서 생성)

// NoteBook 문서 캐시 (쓰기 시 무효화)
const NOTEBOOK_CACHE_PREFIX = 'nb_';
function notebookCacheKey(userId: string, classId: string): string {
  return `${NOTEBOOK_CACHE_PREFIX}${userId}_${classId}`;
}

// NoticeNotes (그룹별) 캐시 (쓰기 시 무효화)
const NOTICE_NOTES_CACHE_PREFIX = 'nn_';
function noticeNotesCacheKey(userId: string, classId: string, memberGroup?: string): string {
  const g = (memberGroup || 'A').toString().trim().toUpperCase() || 'A';
  return `${NOTICE_NOTES_CACHE_PREFIX}${userId}_${classId}_${g}`;
}

// 노트 목록 캐시 (전체 목록 1개 키 per userId, classId; 쓰기 시 무효화)
const NOTES_LIST_CACHE_PREFIX = 'notes_';
function notesListCacheKey(userId: string, classId: string): string {
  return `${NOTES_LIST_CACHE_PREFIX}${userId}_${classId}`;
}

// NoticeBook 문서 캐시 (쓰기 시 무효화)
const NOTICE_BOOK_CACHE_PREFIX = 'nbook_';
function noticeBookCacheKey(userId: string, classId: string): string {
  return `${NOTICE_BOOK_CACHE_PREFIX}${userId}_${classId}`;
}

class BoardService {
  private __noteBookDocCache = new Map<string, { exists: boolean; data: Record<string, any> | null }>();
  private __noticeNotesCache = new Map<string, NoticeNotesDoc>();
  private __notesListCache = new Map<string, NotebookNote[]>();
  private __noticeBookDocCache = new Map<string, { exists: boolean }>();

  private invalidateNoteBookCache(userId: string, classId: string): void {
    this.__noteBookDocCache.delete(notebookCacheKey(userId, classId));
  }

  private invalidateNoticeNotesCache(userId: string, classId: string, memberGroup?: string): void {
    this.__noticeNotesCache.delete(noticeNotesCacheKey(userId, classId, memberGroup));
  }

  private invalidateNotesListCache(userId: string, classId: string): void {
    this.__notesListCache.delete(notesListCacheKey(userId, classId));
  }

  private invalidateNoticeBookCache(userId: string, classId: string): void {
    this.__noticeBookDocCache.delete(noticeBookCacheKey(userId, classId));
  }

  private async getNoticeBookDocCached(userId: string, classId: string): Promise<{ exists: boolean }> {
    const key = noticeBookCacheKey(userId, classId);
    const cached = this.__noticeBookDocCache.get(key);
    if (cached !== undefined) return cached;
    const noticeBookDocRef = this.getNoticeBookDocRef(userId, classId);
    const snap = await getDoc(noticeBookDocRef);
    const exists = snap.exists();
    this.__noticeBookDocCache.set(key, { exists });
    return { exists };
  }

  /** NoteBook 문서 읽기 (캐시 우선, 미스 시 getDoc 후 캐시 저장) */
  private async getNoteBookDocCached(userId: string, classId: string): Promise<{ exists: boolean; data: Record<string, any> | null }> {
    const key = notebookCacheKey(userId, classId);
    const cached = this.__noteBookDocCache.get(key);
    if (cached !== undefined) return cached;
    const noteBookDocRef = this.getNoteBookDocRef(userId, classId);
    const snap = await getDoc(noteBookDocRef);
    const exists = snap.exists();
    const data = exists ? (snap.data() as Record<string, any>) : null;
    this.__noteBookDocCache.set(key, { exists, data });
    return { exists, data };
  }

  // NoteBook 문서 참조
  private getNoteBookDocRef(userId: string, classId: string) {
    // users/{userId}/classes/{classId}/ClassBoard/NoteBook
    const classBoardRef = collection(db, `users/${userId}/classes/${classId}/ClassBoard`);
    return doc(classBoardRef, 'NoteBook');
  }

  // NoticeBook 문서 참조
  private getNoticeBookDocRef(userId: string, classId: string) {
    // users/{userId}/classes/{classId}/ClassBoard/NoticeBook
    const classBoardRef = collection(db, `users/${userId}/classes/${classId}/ClassBoard`);
    return doc(classBoardRef, 'NoticeBook');
  }

  private getNoticeNotesCollectionRef(userId: string, classId: string) {
    // users/{userId}/classes/{classId}/ClassBoard/NoticeBook/NoticeNotes
    return collection(this.getNoticeBookDocRef(userId, classId), 'NoticeNotes');
  }

  private getNoticeNotesMembersCollectionRef(userId: string, classId: string) {
    // users/{userId}/classes/{classId}/ClassBoard/NoticeBook/NoticeNotesMembers
    return collection(this.getNoticeBookDocRef(userId, classId), 'NoticeNotesMembers');
  }

  private getNoticeNoteDocRef(userId: string, classId: string, docId: string) {
    // users/{userId}/classes/{classId}/ClassBoard/NoticeBook/NoticeNotes/NoticeNote-1
    return doc(this.getNoticeNotesCollectionRef(userId, classId), docId);
  }

  private getNoticeNotesMemberDocRef(userId: string, classId: string, memberId: string) {
    // users/{userId}/classes/{classId}/ClassBoard/NoticeBook/NoticeNotesMembers/{memberId}
    return doc(this.getNoticeNotesMembersCollectionRef(userId, classId), memberId);
  }

  private normalizeMemberGroup(memberGroup?: string): string {
    const normalized = (memberGroup || 'A').toString().trim().toUpperCase();
    return normalized.length > 0 ? normalized : 'A';
  }

  private getNoticeNoteDocId(noteNumber: number, memberGroup?: string): string {
    const group = this.normalizeMemberGroup(memberGroup);
    // A 그룹은 기존 문서 ID를 유지 (하위 호환성)
    if (group === 'A') return `NoticeNote-${noteNumber}`;
    return `NoticeNote_${group}-${noteNumber}`;
  }

  private getNoticeNoteDocIdPrefix(memberGroup?: string): string {
    const group = this.normalizeMemberGroup(memberGroup);
    if (group === 'A') return 'NoticeNote-';
    return `NoticeNote_${group}-`;
  }

  private async getNoticeNoteDocsSnapshot(userId: string, classId: string, memberGroup?: string) {
    const notesCollectionRef = this.getNoticeNotesCollectionRef(userId, classId);
    const prefix = this.getNoticeNoteDocIdPrefix(memberGroup);
    // 문서 ID(prefix) 범위 쿼리로 그룹별 NoticeNote 문서만 읽어서 비용/혼합을 방지
    const q = query(
      notesCollectionRef,
      orderBy(documentId()),
      startAt(prefix),
      endAt(prefix + '\uf8ff')
    );
    return await getDocs(q);
  }

  private async getMemberGroupsForClass(userId: string, classId: string): Promise<string[]> {
    try {
      const classRef = doc(db, `users/${userId}/classes/${classId}`);
      const classSnap = await getDoc(classRef);
      const raw = classSnap.exists() ? (classSnap.data() as any)?.memberGroups : null;
      if (Array.isArray(raw) && raw.length > 0) {
        return raw.map((g) => (g || 'A').toString().trim().toUpperCase());
      }
    } catch {
      // ignore
    }
    return ['A'];
  }


  // 카테고리는 NoteBook 문서의 categories 배열 필드로 저장됨 (더 이상 컬렉션 불필요)

  // notes 서브컬렉션 참조
  private getNotesCollectionRef(userId: string, classId: string) {
    // users/{userId}/classes/{classId}/ClassBoard/NoteBook/notes
    return collection(this.getNoteBookDocRef(userId, classId), 'notes');
  }

  /** categorizedNotes[categoryId] 값을 배열로 정규화 (V2 객체 { [noteId]: NotebookNote } 또는 레거시 배열 지원) */
  private categoryNotesToArray(raw: any): any[] {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'object') return Object.values(raw);
    return [];
  }

  /** 노트 배열을 V2 객체 { [noteId]: NotebookNote } 로 변환 (저장용) */
  private categoryNotesToObject(notes: any[]): Record<string, any> {
    const obj: Record<string, any> = {};
    for (const n of notes) {
      if (n?.id) obj[n.id] = n;
    }
    return obj;
  }

  // note 문서 참조 (예: note-1, note-2)
  private getNoteDocRef(userId: string, classId: string, noteDocId: string) {
    // users/{userId}/classes/{classId}/ClassBoard/NoteBook/notes/note-1
    return doc(this.getNotesCollectionRef(userId, classId), noteDocId);
  }

  // NoteBook 문서 초기화 (없으면 생성). 캐시 사용, 쓰기 후 무효화.
  private async ensureNoteBookDoc(userId: string, classId: string): Promise<void> {
    try {
      const { exists, data } = await this.getNoteBookDocCached(userId, classId);
      const noteBookDocRef = this.getNoteBookDocRef(userId, classId);

      if (!exists) {
        await setDoc(noteBookDocRef, {
          categories: [],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        this.invalidateNoteBookCache(userId, classId);
      } else if (data?.categories === undefined) {
        await updateDoc(noteBookDocRef, {
          categories: []
        });
        this.invalidateNoteBookCache(userId, classId);
      }
    } catch (error) {
      console.error('❌ NoteBook 문서 초기화 실패:', error);
      throw error;
    }
  }

  // NoticeBook 문서 초기화 (캐시 사용; 없으면 생성 후 캐시 무효화)
  private async ensureNoticeBookDoc(userId: string, classId: string): Promise<void> {
    try {
      const { exists } = await this.getNoticeBookDocCached(userId, classId);
      if (exists) return;

      const noticeBookDocRef = this.getNoticeBookDocRef(userId, classId);
      await setDoc(noticeBookDocRef, {
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      this.invalidateNoticeBookCache(userId, classId);
    } catch (error: any) {
      // 오프라인 상태에서는 에러를 무시하고 계속 진행 (오프라인 캐시 사용)
      if (error?.code === 'unavailable' || error?.message?.includes('offline')) {
        console.log('⚠️ NoticeBook 문서 초기화: 오프라인 상태, 캐시 사용');
        return;
      }
      console.error('❌ NoticeBook 문서 초기화 실패:', error);
      throw error;
    }
  }

  // 그룹별 NoticeNote-1 문서 초기화 (없으면 생성)
  private async ensureFirstNoticeNoteDoc(
    userId: string,
    classId: string,
    memberGroup?: string
  ): Promise<string> {
    try {
      await this.ensureNoticeBookDoc(userId, classId);

      const firstDocId = this.getNoticeNoteDocId(1, memberGroup);
      const noteRef = this.getNoticeNoteDocRef(userId, classId, firstDocId);
      const snap = await getDoc(noteRef);

      if (!snap.exists()) {
        await setDoc(noteRef, {
          // V2: 빈 객체로 초기화 (부분 업데이트 가능)
          GeneralNoticeAll: {},
          GeneralNoticeGroup: {},
          // legacy field 유지(읽기 하위호환)
          GeneralNotice: [],
          IndividualNotice: {},
          totalSize: 0, // 용량 추적 필드 (bytes)
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } else {
        // 기존 문서에 필드가 없으면 보강 (한글 필드명도 마이그레이션)
        const data = snap.data();
        const updates: Record<string, any> = {};
        
        // 한글 필드명이 있으면 영어로 마이그레이션
        // legacy: 한글 필드명이 있으면 영어로 마이그레이션
        if (data?.['전체공지노트'] !== undefined && data?.['GeneralNotice'] === undefined) {
          updates['GeneralNotice'] = data['전체공지노트'];
        } else if (data?.['GeneralNotice'] === undefined) {
          updates['GeneralNotice'] = [];
        }

        // new fields가 없으면 보강 (기존 배열 데이터는 마이그레이션에서 V2 객체로 변환)
        if (data?.['GeneralNoticeAll'] === undefined) {
          updates['GeneralNoticeAll'] = data?.['GeneralNotice'] ?? [];
        }
        if (data?.['GeneralNoticeGroup'] === undefined) {
          updates['GeneralNoticeGroup'] = [];
        }
        if (data?.['개별공지노트'] !== undefined && data?.['IndividualNotice'] === undefined) {
          updates['IndividualNotice'] = data['개별공지노트'];
        } else if (data?.['IndividualNotice'] === undefined) {
          updates['IndividualNotice'] = [];
        }
        
        // totalSize 필드가 없으면 추가
        if (data?.totalSize === undefined) {
          const currentSize = this.estimateDocSize(data);
          updates['totalSize'] = currentSize;
        }
        
        if (Object.keys(updates).length > 0) {
          await updateDoc(noteRef, updates);
        }
      }
      
      return firstDocId;
    } catch (error: any) {
      // 오프라인 상태에서는 에러를 무시하고 계속 진행 (오프라인 캐시 사용)
      if (error?.code === 'unavailable' || error?.message?.includes('offline')) {
        const firstDocId = this.getNoticeNoteDocId(1, memberGroup);
        console.log(`⚠️ ${firstDocId} 문서 초기화: 오프라인 상태, 캐시 사용`);
        return firstDocId;
      }
      console.error('❌ NoticeNote-1 문서 초기화 실패:', error);
      throw error;
    }
  }

  // 사용 가능한 NoticeNote 문서 ID 찾기 (용량이 여유있는 것)
  private async findAvailableNoticeNoteDocId(
    userId: string,
    classId: string,
    memberGroup?: string
  ): Promise<string> {
    try {
      // NoticeNote-1부터 순차적으로 확인
      let noteNumber = 1;
      
      while (true) {
        const noteDocId = this.getNoticeNoteDocId(noteNumber, memberGroup);
        const noteDocRef = this.getNoticeNoteDocRef(userId, classId, noteDocId);
        const noteDoc = await getDoc(noteDocRef);
        
        if (!noteDoc.exists()) {
          // V2: 빈 객체로 생성
          await setDoc(noteDocRef, {
            GeneralNotice: [],
            GeneralNoticeAll: {},
            GeneralNoticeGroup: {},
            IndividualNotice: {},
            totalSize: 0, // 용량 추적 필드 (bytes)
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
          return noteDocId;
        }
        
        // 문서가 있으면 크기 확인
        const data = noteDoc.data();
        let currentSize: number;
        
        // totalSize 필드가 있으면 사용, 없으면 추정
        if (data?.totalSize !== undefined && typeof data.totalSize === 'number') {
          currentSize = data.totalSize;
        } else {
          // 기존 문서에 totalSize가 없으면 추정하고 업데이트
          currentSize = this.estimateDocSize(data);
          await updateDoc(noteDocRef, {
            totalSize: currentSize
          });
        }
        
        if (currentSize < MAX_NOTE_DOC_SIZE) {
          // 용량이 여유있으면 사용
          return noteDocId;
        }
        
        // 용량이 가득 차면 다음 번호로
        noteNumber++;
      }
    } catch (error) {
      console.error('❌ 사용 가능한 NoticeNote 문서 찾기 실패:', error);
      throw error;
    }
  }

  private normalizeNoticeEntries(entries: any): NoticeNoteEntry[] {
    const arr = Array.isArray(entries) ? entries : [];
    return arr.map((e: any) => {
      const createdAt =
        e?.createdAt instanceof Timestamp
          ? e.createdAt
          : e?.createdAt?.seconds !== undefined
            ? new Timestamp(e.createdAt.seconds, e.createdAt.nanoseconds || 0)
            : Timestamp.now();
      const updatedAt =
        e?.updatedAt instanceof Timestamp
          ? e.updatedAt
          : e?.updatedAt?.seconds !== undefined
            ? new Timestamp(e.updatedAt.seconds, e.updatedAt.nanoseconds || 0)
            : Timestamp.now();
      return {
        content: e?.content || '',
        fontSize: typeof e?.fontSize === 'number' ? e.fontSize : 16,
        date: e?.date || '',
        createdAt,
        updatedAt,
      };
    });
  }

  private normalizeIndividualNoticeBuckets(raw: any): IndividualNoticeStudentBucket[] {
    const arr = Array.isArray(raw) ? raw : [];
    // legacy: IndividualNotice가 NoticeNoteEntry[]였던 경우(학생 구분 없음)
    const looksLikeEntryArray =
      arr.length > 0 && (arr[0]?.content !== undefined || arr[0]?.createdAt !== undefined) && arr[0]?.studentId === undefined;

    if (looksLikeEntryArray) {
      return [
        {
          studentId: '__legacy__',
          studentName: 'legacy',
          notes: this.normalizeNoticeEntries(arr),
        },
      ];
    }

    return arr
      .map((b: any) => ({
        studentId: (b?.studentId || '').toString(),
        studentName: b?.studentName ? (b.studentName || '').toString() : undefined,
        notes: this.normalizeNoticeEntries(b?.notes),
      }))
      .filter((b: IndividualNoticeStudentBucket) => !!b.studentId);
  }

  /** V2: 객체 형태 GeneralNoticeAll/GeneralNoticeGroup → 배열로 변환 (듀얼 읽기) */
  private generalNoticeObjectToArray(raw: any): NoticeNoteEntry[] {
    if (Array.isArray(raw)) return this.normalizeNoticeEntries(raw);
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const entries = Object.values(raw) as any[];
      return this.normalizeNoticeEntries(entries);
    }
    return [];
  }

  /** V2: 객체 형태 IndividualNotice → IndividualNoticeStudentBucket[] 변환 (듀얼 읽기) */
  private individualNoticeObjectToBuckets(raw: any): IndividualNoticeStudentBucket[] {
    if (Array.isArray(raw)) return this.normalizeIndividualNoticeBuckets(raw);
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const buckets: IndividualNoticeStudentBucket[] = [];
      for (const [studentId, bucketAny] of Object.entries(raw)) {
        if (!studentId) continue;
        const b = bucketAny as any;
        const notesRaw = b?.notes;
        const notes = Array.isArray(notesRaw)
          ? this.normalizeNoticeEntries(notesRaw)
          : notesRaw && typeof notesRaw === 'object'
            ? this.normalizeNoticeEntries(Object.values(notesRaw))
            : [];
        buckets.push({
          studentId,
          studentName: b?.studentName,
          notes,
        });
      }
      return buckets;
    }
    return [];
  }

  /** V2: 공지 엔트리 배열 → 객체 (저장용) */
  private generalNoticeArrayToObject(entries: NoticeNoteEntry[]): GeneralNoticeEntriesV2 {
    const obj: GeneralNoticeEntriesV2 = {};
    for (const e of entries) {
      obj[getNoticeEntryKey(e)] = e;
    }
    return obj;
  }

  /** V2: 개별공지 버킷 배열 → 객체 (저장용) */
  private individualNoticeBucketsToObject(buckets: IndividualNoticeStudentBucket[]): IndividualNoticeV2 {
    const obj: IndividualNoticeV2 = {};
    for (const b of buckets) {
      const notesObj: Record<string, NoticeNoteEntry> = {};
      for (const n of b.notes) {
        notesObj[getNoticeEntryKey(n)] = n;
      }
      obj[b.studentId] = { studentName: b.studentName, notes: notesObj };
    }
    return obj;
  }

  /** V2: raw 필드값을 공지 객체로 반환 (배열이면 변환, 객체면 그대로) */
  private getGeneralNoticeAsObject(raw: any): GeneralNoticeEntriesV2 {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw as GeneralNoticeEntriesV2;
    return this.generalNoticeArrayToObject(this.generalNoticeObjectToArray(raw || []));
  }

  /** V2: raw 필드값을 IndividualNotice 객체로 반환 (배열이면 변환, 객체면 그대로) */
  private getIndividualNoticeAsObject(raw: any): IndividualNoticeV2 {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw as IndividualNoticeV2;
    return this.individualNoticeBucketsToObject(this.individualNoticeObjectToBuckets(raw || []));
  }

  async getNoticeNotes(userId: string, classId: string, memberGroup?: string): Promise<NoticeNotesDoc> {
    const normalizedGroup = this.normalizeMemberGroup(memberGroup);
    const cacheKey = noticeNotesCacheKey(userId, classId, normalizedGroup);
    const cached = this.__noticeNotesCache.get(cacheKey);
    if (cached !== undefined) return cached;

    await this.ensureFirstNoticeNoteDoc(userId, classId, memberGroup);

    // 그룹별 NoticeNote 문서에서만 데이터 가져오기
    const snapshot = await this.getNoticeNoteDocsSnapshot(userId, classId, memberGroup);
    
    const allGeneralNotice: NoticeNoteEntry[] = [];
    const allIndividualBucketsByStudentId: Record<string, IndividualNoticeStudentBucket> = {};
    let latestCreatedAt = Timestamp.now();
    let latestUpdatedAt = Timestamp.now();
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      // legacy는 GeneralNoticeAll이 없는 경우에만 포함(중복 방지)
      const legacyGeneralNotice =
        data?.['GeneralNoticeAll'] === undefined ? (data?.['GeneralNotice'] ?? data?.['전체공지노트'] ?? []) : [];
      // V2 듀얼 읽기: 배열 또는 객체 { [entryKey]: NoticeNoteEntry } 모두 처리
      const generalNoticeAll = this.generalNoticeObjectToArray(data?.['GeneralNoticeAll'] ?? []);
      const generalNoticeGroup = this.generalNoticeObjectToArray(data?.['GeneralNoticeGroup'] ?? []);
      const individualNoticeRaw = data?.['IndividualNotice'] ?? data?.['개별공지노트'] ?? [];
      const buckets = this.individualNoticeObjectToBuckets(individualNoticeRaw);
      
      allGeneralNotice.push(
        ...generalNoticeAll.map((e) => ({ ...e, scope: 'all' as const })),
        ...this.normalizeNoticeEntries(legacyGeneralNotice).map((e) => ({ ...e, scope: 'all' as const })),
        ...generalNoticeGroup.map((e) => ({ ...e, scope: 'group' as const }))
      );
      buckets.forEach((b) => {
        const key = b.studentId;
        if (!allIndividualBucketsByStudentId[key]) {
          allIndividualBucketsByStudentId[key] = { studentId: key, studentName: b.studentName, notes: [] };
        }
        allIndividualBucketsByStudentId[key].studentName =
          allIndividualBucketsByStudentId[key].studentName || b.studentName;
        allIndividualBucketsByStudentId[key].notes.push(...b.notes);
      });
      
      // 가장 최근 타임스탬프 추적
      if (data?.createdAt instanceof Timestamp) {
        if (data.createdAt.toMillis() > latestCreatedAt.toMillis()) {
          latestCreatedAt = data.createdAt;
        }
      }
      if (data?.updatedAt instanceof Timestamp) {
        if (data.updatedAt.toMillis() > latestUpdatedAt.toMillis()) {
          latestUpdatedAt = data.updatedAt;
        }
      }
    });
    
    // createdAt 기준 내림차순 정렬
    allGeneralNotice.sort((a, b) => {
      const aTime = a.createdAt?.toMillis() || 0;
      const bTime = b.createdAt?.toMillis() || 0;
      return bTime - aTime;
    });

    // 개별공지: 학생별로 정렬(각 학생 내부는 createdAt desc)
    const allIndividualNotice: IndividualNoticeStudentBucket[] = Object.values(allIndividualBucketsByStudentId);
    allIndividualNotice.forEach((b) => {
      b.notes.sort((a, c) => (c.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
    });
    
    const result: NoticeNotesDoc = {
      GeneralNotice: allGeneralNotice,
      IndividualNotice: allIndividualNotice,
      createdAt: latestCreatedAt,
      updatedAt: latestUpdatedAt,
    };
    this.__noticeNotesCache.set(cacheKey, result);
    return result;
  }

  private async rebuildNoticeNotesMember(
    userId: string,
    classId: string,
    memberId: string,
    memberGroup?: string
  ): Promise<void> {
    const normalizedGroup = this.normalizeMemberGroup(memberGroup);
    const groupData = await this.getNoticeNotes(userId, classId, normalizedGroup);
    const generalNotices = groupData.GeneralNotice || [];
    const bucket = (groupData.IndividualNotice || []).find((b) => b.studentId === memberId);
    const individualNotesArray = bucket?.notes || [];

    // V2: NoticeNote-1과 동일하게 객체로 저장
    const generalNoticeAllObj = this.generalNoticeArrayToObject(generalNotices);
    const individualNoticesObj: Record<string, NoticeNoteEntry> = {};
    for (const n of individualNotesArray) {
      individualNoticesObj[getNoticeEntryKey(n)] = n;
    }

    await setDoc(
      this.getNoticeNotesMemberDocRef(userId, classId, memberId),
      {
        memberId,
        memberGroup: normalizedGroup,
        generalNoticeAll: generalNoticeAllObj,
        individualNotices: individualNoticesObj,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }

  private async rebuildNoticeNotesMembersForGroup(
    userId: string,
    classId: string,
    memberGroup?: string
  ): Promise<void> {
    const normalizedGroup = this.normalizeMemberGroup(memberGroup);
    const groupData = await this.getNoticeNotes(userId, classId, normalizedGroup);
    const generalNotices = groupData.GeneralNotice || [];
    const buckets = groupData.IndividualNotice || [];

    // V2: NoticeNote-1과 동일하게 객체로 저장
    const generalNoticeAllObj = this.generalNoticeArrayToObject(generalNotices);

    const { ClassMembersPartitionService } = await import('./classMembersPartitionService');
    const partitionService = new ClassMembersPartitionService(userId, classId, normalizedGroup);
    const members = await partitionService.loadAllMembers();

    let batch = writeBatch(db);
    let opCount = 0;

    const commitBatch = async () => {
      if (opCount === 0) return;
      await batch.commit();
      batch = writeBatch(db);
      opCount = 0;
    };

    for (const member of members) {
      const bucket = buckets.find((b) => b.studentId === member.id);
      const individualNotesArray = bucket?.notes || [];
      const individualNoticesObj: Record<string, NoticeNoteEntry> = {};
      for (const n of individualNotesArray) {
        individualNoticesObj[getNoticeEntryKey(n)] = n;
      }
      const docRef = this.getNoticeNotesMemberDocRef(userId, classId, member.id);
      batch.set(
        docRef,
        {
          memberId: member.id,
          memberGroup: normalizedGroup,
          generalNoticeAll: generalNoticeAllObj,
          individualNotices: individualNoticesObj,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      opCount += 1;
      if (opCount >= 400) {
        await commitBatch();
      }
    }
    await commitBatch();
  }

  private async rebuildNoticeNotesMembersForAllGroups(
    userId: string,
    classId: string
  ): Promise<void> {
    const groups = await this.getMemberGroupsForClass(userId, classId);
    for (const group of groups) {
      await this.rebuildNoticeNotesMembersForGroup(userId, classId, group);
    }
  }

  private async syncNoticeNotesMembersForGeneralNotice(
    userId: string,
    classId: string,
    scope: 'all' | 'group',
    memberGroup?: string
  ): Promise<void> {
    if (scope === 'all') {
      await this.rebuildNoticeNotesMembersForAllGroups(userId, classId);
      return;
    }
    await this.rebuildNoticeNotesMembersForGroup(userId, classId, memberGroup);
  }

  /** NoticeNotesMembers 문서 raw(배열 또는 V2 객체) → NoticeNotesMemberDoc(배열) 정규화 */
  private memberDocRawToNormalized(raw: Record<string, any>): NoticeNotesMemberDoc {
    const generalNoticeAll = Array.isArray(raw.generalNoticeAll)
      ? raw.generalNoticeAll
      : raw.generalNoticeAll && typeof raw.generalNoticeAll === 'object'
        ? this.normalizeNoticeEntries(Object.values(raw.generalNoticeAll))
        : [];
    const generalNoticeGroup = Array.isArray(raw.generalNoticeGroup)
      ? raw.generalNoticeGroup
      : raw.generalNoticeGroup && typeof raw.generalNoticeGroup === 'object'
        ? this.normalizeNoticeEntries(Object.values(raw.generalNoticeGroup))
        : [];
    const individualNotices = Array.isArray(raw.individualNotices)
      ? raw.individualNotices
      : raw.individualNotices && typeof raw.individualNotices === 'object'
        ? this.normalizeNoticeEntries(Object.values(raw.individualNotices))
        : [];
    return {
      memberId: raw.memberId ?? '',
      memberGroup: raw.memberGroup,
      generalNoticeAll,
      generalNoticeGroup: generalNoticeGroup.length ? generalNoticeGroup : undefined,
      individualNotices,
      createdAt: raw.createdAt,
      updatedAt: raw.updatedAt,
    };
  }

  async getNoticeNotesForMember(
    userId: string,
    classId: string,
    memberId: string
  ): Promise<NoticeNotesMemberDoc | null> {
    const docRef = this.getNoticeNotesMemberDocRef(userId, classId, memberId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    const raw = snap.data() as Record<string, any>;
    return this.memberDocRawToNormalized(raw);
  }

  async getGeneralNoticeNotesForMember(
    userId: string,
    classId: string,
    memberId: string
  ): Promise<NoticeNoteEntry[]> {
    const data = await this.getNoticeNotesForMember(userId, classId, memberId);
    if (!data) return [];
    return data.generalNoticeAll || [];
  }

  async getIndividualNoticeNotesForMember(
    userId: string,
    classId: string,
    memberId: string
  ): Promise<NoticeNoteEntry[]> {
    const data = await this.getNoticeNotesForMember(userId, classId, memberId);
    return data?.individualNotices || [];
  }

  async ensureNoticeNotesMemberDoc(
    userId: string,
    classId: string,
    memberId: string,
    memberGroup?: string
  ): Promise<void> {
    await this.rebuildNoticeNotesMember(userId, classId, memberId, memberGroup);
  }

  async deleteNoticeNotesMemberDoc(
    userId: string,
    classId: string,
    memberId: string
  ): Promise<void> {
    try {
      await deleteDoc(this.getNoticeNotesMemberDocRef(userId, classId, memberId));
    } catch {
      // ignore
    }
  }

  async syncNoticeNotesMembersForGroup(
    userId: string,
    classId: string,
    memberGroup?: string
  ): Promise<void> {
    await this.rebuildNoticeNotesMembersForGroup(userId, classId, memberGroup);
  }

  async syncNoticeNotesMembersForAllGroups(
    userId: string,
    classId: string
  ): Promise<void> {
    await this.rebuildNoticeNotesMembersForAllGroups(userId, classId);
  }

  async migrateNoticeNotesMembers(userId: string, classId: string): Promise<void> {
    await this.rebuildNoticeNotesMembersForAllGroups(userId, classId);
  }

  // 공지 노트 저장 (파티션 기능 적용)
  async saveNoticeNote(
    userId: string,
    classId: string,
    field: NoticeNoteField,
    content: string,
    date: string,
    createdAtTimestamp?: number | null,
    memberGroup?: string,
    generalNoticeScope: 'all' | 'group' = 'all'
  ): Promise<void> {
    await this.ensureFirstNoticeNoteDoc(userId, classId, memberGroup);

    // 그룹별 NoticeNote 문서에서만 데이터 가져오기
    const snapshot = await this.getNoticeNoteDocsSnapshot(userId, classId, memberGroup);

    const resolvedField =
      field === 'GeneralNotice'
        ? (generalNoticeScope === 'group' ? 'GeneralNoticeGroup' : 'GeneralNoticeAll')
        : field;
    
    let targetDocId: string | null = null;
    let targetDocRef: any = null;
    let existingData: any = null;
    let existingEntries: any[] = [];
    let entryIndex: number = -1;
    
    // createdAtTimestamp가 있으면 기존 노트를 찾아서 업데이트
    if (createdAtTimestamp) {
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const entries = this.generalNoticeObjectToArray(data?.[resolvedField] || []);
        const index = entries.findIndex((e: any) => {
          const eTimestamp = e?.createdAt instanceof Timestamp
            ? e.createdAt.toMillis()
            : e?.createdAt?.seconds !== undefined
              ? e.createdAt.seconds * 1000 + (e.createdAt.nanoseconds || 0) / 1000000
              : null;
          return eTimestamp === createdAtTimestamp;
        });
        if (index !== -1) {
          targetDocId = docSnap.id;
          targetDocRef = docSnap.ref;
          existingData = data;
          existingEntries = entries;
          entryIndex = index;
          break;
        }
      }
    }
    if (!targetDocRef) {
      targetDocId = await this.findAvailableNoticeNoteDocId(userId, classId, memberGroup);
      targetDocRef = this.getNoticeNoteDocRef(userId, classId, targetDocId);
      const noteDoc = await getDoc(targetDocRef);
      existingData = noteDoc.data() || {};
      existingEntries = this.generalNoticeObjectToArray(existingData?.[resolvedField] || []);
      entryIndex = -1;
    }
    const now = Timestamp.now();
    const currentObj = this.getGeneralNoticeAsObject(existingData?.[resolvedField]);
    if (entryIndex !== -1) {
      const existingEntry = existingEntries[entryIndex];
      const updatedEntry: NoticeNoteEntry = {
        content: content || '',
        date: date || '',
        createdAt: existingEntry.createdAt,
        updatedAt: now,
      };
      const key = getNoticeEntryKey(existingEntry);
      currentObj[key] = updatedEntry;
    } else {
      const createdAt =
        typeof createdAtTimestamp === 'number' ? Timestamp.fromMillis(createdAtTimestamp) : now;
      const entry: NoticeNoteEntry = {
        content: content || '',
        date: date || '',
        createdAt,
        updatedAt: now,
      };
      currentObj[getNoticeEntryKey(entry)] = entry;
    }
    const updatedDocData = {
      ...existingData,
      [resolvedField]: currentObj,
      updatedAt: serverTimestamp()
    };
    const estimatedTotalSize = this.estimateDocSize(updatedDocData);
    await updateDoc(targetDocRef, {
      [resolvedField]: currentObj,
      totalSize: estimatedTotalSize,
      updatedAt: serverTimestamp()
    } as any);

    if (field === 'GeneralNotice') {
      await this.syncNoticeNotesMembersForGeneralNotice(
        userId,
        classId,
        generalNoticeScope,
        memberGroup
      );
    }
    this.invalidateNoticeNotesCache(userId, classId, memberGroup);
  }

  // 전체공지 노트 목록 조회 (노트 목록 모달용)
  async getGeneralNoticeNotes(userId: string, classId: string, memberGroup?: string): Promise<NoticeNoteEntry[]> {
    try {
      const data = await this.getNoticeNotes(userId, classId, memberGroup);
      // new+legacy 통합 반환 (scope 포함)
      return data.GeneralNotice || [];
    } catch (error) {
      console.error('❌ 전체공지 노트 목록 조회 실패:', error);
      return [];
    }
  }

  // 특정 학생의 개별공지 노트 목록 조회 (학생 선택 모달/목록용)
  async getIndividualNoticeNotesForStudent(
    userId: string,
    classId: string,
    studentId: string,
    memberGroup?: string
  ): Promise<NoticeNoteEntry[]> {
    try {
      const data = await this.getNoticeNotes(userId, classId, memberGroup);
      const bucket = (data.IndividualNotice || []).find((b) => b.studentId === studentId);
      return bucket?.notes || [];
    } catch (error) {
      console.error('❌ 개별공지 노트 목록 조회 실패:', error);
      return [];
    }
  }

  // 모든 개별공지 노트 목록 조회 (날짜별 조회용)
  async getAllIndividualNoticeNotes(
    userId: string,
    classId: string,
    memberGroup?: string
  ): Promise<Array<NoticeNoteEntry & { studentId: string; studentName?: string }>> {
    try {
      const data = await this.getNoticeNotes(userId, classId, memberGroup);
      const allNotes: Array<NoticeNoteEntry & { studentId: string; studentName?: string }> = [];
      
      (data.IndividualNotice || []).forEach((bucket) => {
        bucket.notes.forEach((note) => {
          allNotes.push({
            ...note,
            studentId: bucket.studentId,
            studentName: bucket.studentName,
          });
        });
      });
      
      return allNotes;
    } catch (error) {
      console.error('❌ 모든 개별공지 노트 목록 조회 실패:', error);
      return [];
    }
  }

  // 여러 학생의 개별공지 노트를 한 번에 저장 (문서를 한 번만 읽고 한 번만 업데이트)
  async saveMultipleIndividualNoticeNotes(
    userId: string,
    classId: string,
    notes: Array<{
      studentId: string;
      studentName?: string;
      content: string;
      fontSize: number;
      date: string;
    }>,
    batchTimestamp?: number // 같은 저장 배치임을 나타내는 타임스탬프 (선택적)
    ,
    memberGroup?: string
  ): Promise<void> {
    if (notes.length === 0) return;

    await this.ensureFirstNoticeNoteDoc(userId, classId, memberGroup);

    console.log(`💾 [saveMultipleIndividualNoticeNotes] 시작: ${notes.length}명 학생의 노트 저장`);

    // batchTimestamp가 제공되면 같은 타임스탬프를 사용, 아니면 현재 시간 사용
    const now = batchTimestamp 
      ? Timestamp.fromMillis(batchTimestamp)
      : Timestamp.now();

    // 사용 가능한 문서 찾기
    const noteDocId = await this.findAvailableNoticeNoteDocId(userId, classId, memberGroup);
    const noteDocRef = this.getNoticeNoteDocRef(userId, classId, noteDocId);

    console.log(`📄 사용할 문서: ${noteDocId}`);

    // 문서 읽기 (V2: 배열/객체 듀얼)
    const noteDoc = await getDoc(noteDocRef);
    const existingData = noteDoc.data() || {};
    const rawIndividual = existingData?.['IndividualNotice'] ?? existingData?.['개별공지노트'] ?? [];
    const buckets = this.individualNoticeObjectToBuckets(rawIndividual);

    // 모든 학생의 노트를 추가
    const updatedBuckets = [...buckets];
    let addedCount = 0;

    for (const noteData of notes) {
      const entry: NoticeNoteEntry = {
        content: noteData.content || '',
        fontSize: Math.max(12, Math.min(noteData.fontSize || 16, 64)),
        date: noteData.date || '',
        createdAt: now,
        updatedAt: now,
      };

      // 기존 버킷 찾기
      const existingBucketIndex = updatedBuckets.findIndex(b => b.studentId === noteData.studentId);
      
      if (existingBucketIndex !== -1) {
        // 기존 버킷에 추가
        updatedBuckets[existingBucketIndex] = {
          ...updatedBuckets[existingBucketIndex],
          studentName: updatedBuckets[existingBucketIndex].studentName || noteData.studentName,
          notes: [...(updatedBuckets[existingBucketIndex].notes || []), entry],
        };
      } else {
        // 새 버킷 생성
        updatedBuckets.push({
          studentId: noteData.studentId,
          studentName: noteData.studentName,
          notes: [entry],
        });
      }
      addedCount++;
    }

    console.log(`✅ ${addedCount}명의 노트 추가 완료`);

    // V2: 객체로 저장
    const individualObj = this.individualNoticeBucketsToObject(updatedBuckets);
    const updatedDocData = {
      ...existingData,
      IndividualNotice: individualObj,
      updatedAt: serverTimestamp(),
    };
    const estimatedTotalSize = this.estimateDocSize(updatedDocData);

    await updateDoc(noteDocRef, {
      IndividualNotice: individualObj,
      totalSize: estimatedTotalSize,
      updatedAt: serverTimestamp(),
    } as any);

    console.log(`💾 문서 ${noteDocId} 업데이트 완료`);

    const uniqueStudentIds = Array.from(new Set(notes.map((n) => n.studentId)));
    for (const studentId of uniqueStudentIds) {
      await this.rebuildNoticeNotesMember(userId, classId, studentId, memberGroup);
    }
    this.invalidateNoticeNotesCache(userId, classId, memberGroup);
  }

  // 학생별 개별공지 저장 (파티션 기능 적용)
  async saveIndividualNoticeNoteForStudent(
    userId: string,
    classId: string,
    studentId: string,
    studentName: string | undefined,
    content: string,
    fontSize: number,
    date: string,
    batchTimestamp?: number, // 같은 저장 배치임을 나타내는 타임스탬프 (선택적)
    memberGroup?: string
  ): Promise<void> {
    await this.ensureFirstNoticeNoteDoc(userId, classId, memberGroup);

    const noteDocId = await this.findAvailableNoticeNoteDocId(userId, classId, memberGroup);
    const noteDocRef = this.getNoticeNoteDocRef(userId, classId, noteDocId);

    const noteDoc = await getDoc(noteDocRef);
    const existingData = noteDoc.data() || {};
    const rawIndividual = existingData?.['IndividualNotice'] ?? existingData?.['개별공지노트'] ?? [];
    const buckets = this.individualNoticeObjectToBuckets(rawIndividual);

    const now = batchTimestamp 
      ? Timestamp.fromMillis(batchTimestamp)
      : Timestamp.now();
    const entry: NoticeNoteEntry = {
      content: content || '',
      fontSize: Math.max(12, Math.min(fontSize || 16, 64)),
      date: date || '',
      createdAt: now,
      updatedAt: now,
    };

    let didUpdate = false;
    const updatedBuckets = buckets.map((b) => {
      if (b.studentId !== studentId) return b;
      didUpdate = true;
      return {
        ...b,
        studentName: b.studentName || studentName,
        notes: [...(b.notes || []), entry],
      };
    });

    if (!didUpdate) {
      updatedBuckets.push({
        studentId,
        studentName,
        notes: [entry],
      });
    }

    const individualObj = this.individualNoticeBucketsToObject(updatedBuckets);
    const updatedDocData = {
      ...existingData,
      IndividualNotice: individualObj,
      updatedAt: serverTimestamp(),
    };
    const estimatedTotalSize = this.estimateDocSize(updatedDocData);

    await updateDoc(noteDocRef, {
      IndividualNotice: individualObj,
      totalSize: estimatedTotalSize,
      updatedAt: serverTimestamp(),
    } as any);

    await this.rebuildNoticeNotesMember(userId, classId, studentId, memberGroup);
    this.invalidateNoticeNotesCache(userId, classId, memberGroup);
  }

  // createdAt 타임스탬프를 밀리초로 변환하는 헬퍼 함수
  private getCreatedAtMillis(createdAt: any): number | null {
    if (!createdAt) return null;
    try {
      if (typeof createdAt?.toMillis === 'function') return createdAt.toMillis();
      if (typeof createdAt?.toDate === 'function') return createdAt.toDate().getTime();
      if (typeof createdAt?.seconds === 'number') return createdAt.seconds * 1000;
      if (typeof createdAt?._seconds === 'number') return createdAt._seconds * 1000;
      return null;
    } catch {
      return null;
    }
  }

  /** V2: GeneralNotice 객체에서 timestamp(ms)에 해당하는 엔트리 키 반환 */
  private findGeneralNoticeKeyByTimestamp(obj: Record<string, NoticeNoteEntry>, timestamp: number): string | null {
    if (!obj || typeof obj !== 'object') return null;
    for (const [key, entry] of Object.entries(obj)) {
      const ms = this.getCreatedAtMillis(entry?.createdAt);
      if (ms === timestamp) return key;
    }
    return null;
  }

  /** V2: notes 객체에서 timestamp(ms)에 해당하는 노트 키 반환 */
  private findIndividualNoticeKeyByTimestamp(notesObj: Record<string, NoticeNoteEntry>, timestamp: number): string | null {
    if (!notesObj || typeof notesObj !== 'object') return null;
    for (const [key, entry] of Object.entries(notesObj)) {
      const ms = this.getCreatedAtMillis(entry?.createdAt);
      if (ms === timestamp) return key;
    }
    return null;
  }

  // 여러 학생의 개별공지 노트를 한 번에 수정/추가 (문서를 한 번만 읽고 한 번만 업데이트)
  async updateMultipleIndividualNoticeNotes(
    userId: string,
    classId: string,
    updates: Array<{
      studentId: string;
      studentName?: string;
      timestamp?: number; // 있으면 수정, 없으면 새로 추가
      content: string;
      fontSize: number;
      date: string;
    }>,
    memberGroup?: string
  ): Promise<void> {
    if (updates.length === 0) return;

    await this.ensureFirstNoticeNoteDoc(userId, classId, memberGroup);

    console.log(`✏️ [updateMultipleIndividualNoticeNotes] 시작: ${updates.length}명 학생의 노트 수정/추가`);

    // 그룹별 NoticeNote 문서만 읽기
    const snapshot = await this.getNoticeNoteDocsSnapshot(userId, classId, memberGroup);
    
    console.log(`📄 총 문서 수: ${snapshot.docs.length}개`);

    // 각 문서별로 업데이트 처리
    const docUpdates = new Map<string, {
      doc: any;
      docData: any;
      buckets: any[];
      hasChanges: boolean;
    }>();

    // 모든 문서를 먼저 읽고 버킷 정규화 (V2: 배열/객체 듀얼)
    for (const docSnap of snapshot.docs) {
      const docData = docSnap.data();
      const rawIndividual = docData?.['IndividualNotice'] ?? docData?.['개별공지노트'] ?? [];
      const buckets = this.individualNoticeObjectToBuckets(rawIndividual);
      docUpdates.set(docSnap.id, {
        doc: docSnap.ref,
        docData,
        buckets: [...buckets],
        hasChanges: false,
      });
    }

    // 각 업데이트 처리
    const now = Timestamp.now();
    let updateCount = 0;
    let addCount = 0;

    for (const update of updates) {
      let processed = false;

      // 모든 문서에서 해당 노트 찾기
      for (const [docId, docInfo] of Array.from(docUpdates.entries())) {
        const { buckets } = docInfo;
        
        // 해당 학생의 버킷 찾기
        const studentBucketIndex = buckets.findIndex(
          (bucket: any) => bucket.studentId === update.studentId
        );
        
        if (studentBucketIndex !== -1) {
          const bucket = buckets[studentBucketIndex];
          const notes = bucket.notes || [];
          
          if (update.timestamp !== undefined) {
            // 기존 노트 수정
            const noteIndex = notes.findIndex((note: any) => {
              const noteTimestamp = this.getCreatedAtMillis(note.createdAt);
              return noteTimestamp === update.timestamp;
            });
            
            if (noteIndex !== -1) {
              // 노트 수정
              const updatedNote: NoticeNoteEntry = {
                ...notes[noteIndex],
                content: update.content || '',
                fontSize: Math.max(12, Math.min(update.fontSize || 16, 64)),
                date: update.date || '',
                updatedAt: now,
              };
              
              notes[noteIndex] = updatedNote;
              
              // 버킷 업데이트
              buckets[studentBucketIndex] = {
                ...bucket,
                studentName: bucket.studentName || update.studentName,
                notes,
              };
              
              docInfo.buckets = buckets;
              docInfo.hasChanges = true;
              updateCount++;
              processed = true;
              console.log(`  ✅ 학생 ${update.studentId}: 노트 수정 (문서 ${docId})`);
              break;
            }
          } else {
            // 새 노트 추가 (기존 버킷에)
            const entry: NoticeNoteEntry = {
              content: update.content || '',
              fontSize: Math.max(12, Math.min(update.fontSize || 16, 64)),
              date: update.date || '',
              createdAt: now,
              updatedAt: now,
            };
            
            buckets[studentBucketIndex] = {
              ...bucket,
              studentName: bucket.studentName || update.studentName,
              notes: [...notes, entry],
            };
            
            docInfo.buckets = buckets;
            docInfo.hasChanges = true;
            addCount++;
            processed = true;
            console.log(`  ✅ 학생 ${update.studentId}: 노트 추가 (문서 ${docId})`);
            break;
          }
        }
      }

      // 버킷이 없으면 새로 추가 (사용 가능한 문서에)
      if (!processed && update.timestamp === undefined) {
        // 새 노트 추가인데 버킷이 없으면 사용 가능한 문서에 추가
        const noteDocId = await this.findAvailableNoticeNoteDocId(userId, classId, memberGroup);
        let docInfo = docUpdates.get(noteDocId);
        
        if (!docInfo) {
          const noteDocRef = this.getNoticeNoteDocRef(userId, classId, noteDocId);
          const noteDoc = await getDoc(noteDocRef);
          const docData = noteDoc.data() || {};
          const rawIndividual = docData?.['IndividualNotice'] ?? docData?.['개별공지노트'] ?? [];
          const buckets = this.individualNoticeObjectToBuckets(rawIndividual);
          docInfo = {
            doc: noteDocRef,
            docData,
            buckets: [...buckets],
            hasChanges: false,
          };
          docUpdates.set(noteDocId, docInfo);
        }
        
        const entry: NoticeNoteEntry = {
          content: update.content || '',
          fontSize: Math.max(12, Math.min(update.fontSize || 16, 64)),
          date: update.date || '',
          createdAt: now,
          updatedAt: now,
        };
        
        docInfo.buckets.push({
          studentId: update.studentId,
          studentName: update.studentName,
          notes: [entry],
        });
        
        docInfo.hasChanges = true;
        addCount++;
        console.log(`  ✅ 학생 ${update.studentId}: 새 버킷 생성 및 노트 추가 (문서 ${noteDocId})`);
      } else if (!processed && update.timestamp !== undefined) {
        console.warn(`  ⚠️ 학생 ${update.studentId}: 수정할 노트를 찾을 수 없습니다 (타임스탬프: ${update.timestamp})`);
      }
    }

    console.log(`📊 수정: ${updateCount}개, 추가: ${addCount}개`);

    // 변경된 문서들 업데이트 (V2: 객체로 저장)
    const updatePromises: Promise<void>[] = [];
    for (const [docId, docInfo] of Array.from(docUpdates.entries())) {
      if (docInfo.hasChanges) {
        const individualObj = this.individualNoticeBucketsToObject(docInfo.buckets);
        const updatedDocData = {
          ...docInfo.docData,
          IndividualNotice: individualObj,
          updatedAt: serverTimestamp(),
        };
        const estimatedTotalSize = this.estimateDocSize(updatedDocData);
        updatePromises.push(
          updateDoc(docInfo.doc, {
            IndividualNotice: individualObj,
            totalSize: estimatedTotalSize,
            updatedAt: serverTimestamp(),
          } as any).then(() => {
            console.log(`  💾 문서 ${docId} 업데이트 완료`);
          })
        );
      }
    }

    await Promise.all(updatePromises);
    console.log(`✏️ 모든 노트 수정/추가 완료`);

    const uniqueStudentIds = Array.from(new Set(updates.map((u) => u.studentId)));
    for (const studentId of uniqueStudentIds) {
      await this.rebuildNoticeNotesMember(userId, classId, studentId, memberGroup);
    }
    this.invalidateNoticeNotesCache(userId, classId, memberGroup);
  }

  // 개별공지 노트 수정 (createdAt 타임스탬프 기반)
  async updateIndividualNoticeNoteForStudent(
    userId: string,
    classId: string,
    studentId: string,
    timestamp: number,
    content: string,
    fontSize: number,
    date: string,
    memberGroup?: string
  ): Promise<void> {
    await this.ensureFirstNoticeNoteDoc(userId, classId, memberGroup);

    const snapshot = await this.getNoticeNoteDocsSnapshot(userId, classId, memberGroup);
    let found = false;
    for (const docSnap of snapshot.docs) {
      const docData = docSnap.data();
      const rawIndividual = docData?.['IndividualNotice'] ?? docData?.['개별공지노트'] ?? [];
      const buckets = this.individualNoticeObjectToBuckets(rawIndividual);
      const studentBucketIndex = buckets.findIndex((b: any) => b.studentId === studentId);
      if (studentBucketIndex !== -1) {
        const bucket = buckets[studentBucketIndex];
        const notes = bucket.notes || [];
        const noteIndex = notes.findIndex((note: any) => this.getCreatedAtMillis(note.createdAt) === timestamp);
        if (noteIndex !== -1) {
          const updatedNote: NoticeNoteEntry = {
            ...notes[noteIndex],
            content: content || '',
            fontSize: Math.max(12, Math.min(fontSize || 16, 64)),
            date: date || '',
            updatedAt: Timestamp.now(),
          };
          notes[noteIndex] = updatedNote;
          buckets[studentBucketIndex] = { ...bucket, notes };
          const individualObj = this.individualNoticeBucketsToObject(buckets);
          const estimatedTotalSize = this.estimateDocSize({
            ...docData,
            IndividualNotice: individualObj,
            updatedAt: serverTimestamp(),
          });
          await updateDoc(docSnap.ref, {
            IndividualNotice: individualObj,
            totalSize: estimatedTotalSize,
            updatedAt: serverTimestamp(),
          } as any);
          found = true;
          break;
        }
      }
    }
    
    if (!found) {
      throw new Error('수정할 노트를 찾을 수 없습니다.');
    }

    await this.rebuildNoticeNotesMember(userId, classId, studentId, memberGroup);
    this.invalidateNoticeNotesCache(userId, classId, memberGroup);
  }

  // 전체공지 노트 삭제 (createdAt 타임스탬프 기반)
  async deleteGeneralNoticeNote(
    userId: string,
    classId: string,
    timestamp: number,
    memberGroup?: string
  ): Promise<void> {
    try {
      const snapshot = await this.getNoticeNoteDocsSnapshot(userId, classId, memberGroup);
      const fieldsToCheck: Array<'GeneralNoticeAll' | 'GeneralNoticeGroup' | 'GeneralNotice'> = [
        'GeneralNoticeAll',
        'GeneralNoticeGroup',
        'GeneralNotice',
      ];

      let found = false;
      for (const doc of snapshot.docs) {
        const docData = doc.data();

        for (const field of fieldsToCheck) {
          if (field === 'GeneralNotice' && docData?.['GeneralNoticeAll'] !== undefined) continue;

          const raw = docData?.[field] ?? (field === 'GeneralNotice' ? docData?.['전체공지노트'] : undefined) ?? [];

          // V2: 객체인 경우 deleteField로 해당 키만 삭제
          if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
            const key = this.findGeneralNoticeKeyByTimestamp(raw as Record<string, NoticeNoteEntry>, timestamp);
            if (key != null) {
              const rest = { ...(raw as Record<string, NoticeNoteEntry>) };
              delete rest[key];
              await updateDoc(doc.ref, {
                [`${field}.${key}`]: deleteField(),
                totalSize: this.estimateDocSize({ ...docData, [field]: rest }),
                updatedAt: serverTimestamp(),
              } as any);
              found = true;
              break;
            }
            continue;
          }

          const docGeneralNotices = Array.isArray(raw) ? raw : [];
          if (docGeneralNotices.length === 0) continue;

          const noteIndex = docGeneralNotices.findIndex((note: any) => {
            const noteTimestamp =
              note?.createdAt?.toMillis?.() ||
              (note?.createdAt?.seconds ? note.createdAt.seconds * 1000 : null);
            return noteTimestamp === timestamp;
          });

          if (noteIndex !== -1) {
            const docUpdatedNotices = docGeneralNotices.filter((_: any, i: number) => i !== noteIndex);
            const updatedDocData = { ...docData, [field]: docUpdatedNotices, updatedAt: serverTimestamp() };
            await updateDoc(doc.ref, {
              [field]: docUpdatedNotices,
              totalSize: this.estimateDocSize(updatedDocData),
              updatedAt: serverTimestamp(),
            } as any);
            found = true;
            break;
          }
        }
        if (found) break;
      }

      if (!found) throw new Error('삭제할 노트를 찾을 수 없습니다.');
      await this.rebuildNoticeNotesMembersForAllGroups(userId, classId);
      this.invalidateNoticeNotesCache(userId, classId, memberGroup);
    } catch (error) {
      console.error('❌ 전체공지 노트 삭제 실패:', error);
      throw error;
    }
  }

  // 전체공지 노트 여러 개 한 번에 삭제 (createdAt 타임스탬프 배열 기반)
  async deleteGeneralNoticeNotes(
    userId: string,
    classId: string,
    timestamps: number[],
    memberGroup?: string
  ): Promise<void> {
    try {
      if (!timestamps || timestamps.length === 0) return;

      const snapshot = await this.getNoticeNoteDocsSnapshot(userId, classId, memberGroup);
      const timestampsSet = new Set<number>(timestamps);
      let foundCount = 0;
      const fieldsToCheck: Array<'GeneralNoticeAll' | 'GeneralNoticeGroup' | 'GeneralNotice'> = [
        'GeneralNoticeAll',
        'GeneralNoticeGroup',
        'GeneralNotice',
      ];

      for (const docSnap of snapshot.docs) {
        const docData = docSnap.data();

        for (const field of fieldsToCheck) {
          if (field === 'GeneralNotice' && docData?.['GeneralNoticeAll'] !== undefined) continue;

          const raw = docData?.[field] ?? (field === 'GeneralNotice' ? docData?.['전체공지노트'] : undefined) ?? [];

          // V2: 객체인 경우 삭제할 키들을 찾아 deleteField
          if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
            const obj = raw as Record<string, NoticeNoteEntry>;
            const keysToDelete: string[] = [];
            for (const [key, entry] of Object.entries(obj)) {
              const ms = this.getCreatedAtMillis(entry?.createdAt);
              if (ms != null && timestampsSet.has(ms)) {
                keysToDelete.push(key);
                foundCount += 1;
                timestampsSet.delete(ms);
              }
            }
            if (keysToDelete.length > 0) {
              const rest = { ...obj };
              const updates: Record<string, any> = { updatedAt: serverTimestamp() };
              for (const k of keysToDelete) {
                updates[`${field}.${k}`] = deleteField();
                delete rest[k];
              }
              updates.totalSize = this.estimateDocSize({ ...docData, [field]: rest });
              await updateDoc(docSnap.ref, updates);
            }
            if (timestampsSet.size === 0) break;
            continue;
          }

          const docGeneralNotices = Array.isArray(raw) ? raw : [];
          if (docGeneralNotices.length === 0) continue;

          const updatedNotices: any[] = [];
          for (const note of docGeneralNotices) {
            const noteTimestamp =
              note?.createdAt?.toMillis?.() ||
              (note?.createdAt?.seconds ? note.createdAt.seconds * 1000 : null);
            if (noteTimestamp && timestampsSet.has(noteTimestamp)) {
              foundCount += 1;
              timestampsSet.delete(noteTimestamp);
              continue;
            }
            updatedNotices.push(note);
          }

          if (updatedNotices.length !== docGeneralNotices.length) {
            await updateDoc(docSnap.ref, {
              [field]: updatedNotices,
              totalSize: this.estimateDocSize({ ...docData, [field]: updatedNotices, updatedAt: serverTimestamp() }),
              updatedAt: serverTimestamp(),
            } as any);
          }
          if (timestampsSet.size === 0) break;
        }
        if (timestampsSet.size === 0) break;
      }

      if (foundCount < timestamps.length) {
        console.warn(
          `⚠️ 일부 전체공지 노트를 찾을 수 없습니다. 요청: ${timestamps.length}개, 찾음: ${foundCount}개`
        );
      }
      await this.rebuildNoticeNotesMembersForAllGroups(userId, classId);
      this.invalidateNoticeNotesCache(userId, classId, memberGroup);
    } catch (error) {
      console.error('❌ 전체공지 노트 여러 개 삭제 실패:', error);
      throw error;
    }
  }

  // 개별공지 노트 삭제 (createdAt 타임스탬프 기반, 학생 ID 포함)
  async deleteIndividualNoticeNote(
    userId: string,
    classId: string,
    studentId: string,
    timestamp: number,
    memberGroup?: string
  ): Promise<void> {
    try {
      const snapshot = await this.getNoticeNoteDocsSnapshot(userId, classId, memberGroup);
      let found = false;

      for (const doc of snapshot.docs) {
        const docData = doc.data();
        const rawIndividual = docData?.['IndividualNotice'] ?? docData?.['개별공지노트'] ?? [];

        // V2: 객체인 경우 IndividualNotice[studentId].notes[noteKey] 삭제
        if (rawIndividual && typeof rawIndividual === 'object' && !Array.isArray(rawIndividual)) {
          const bucket = (rawIndividual as Record<string, IndividualNoticeNotesV2>)[studentId];
          if (!bucket?.notes || typeof bucket.notes !== 'object') continue;
          const noteKey = this.findIndividualNoticeKeyByTimestamp(bucket.notes, timestamp);
          if (noteKey == null) continue;

          const restNotes = { ...bucket.notes };
          delete restNotes[noteKey];
          const updates: Record<string, any> = { updatedAt: serverTimestamp() };
          if (Object.keys(restNotes).length === 0) {
            updates[`IndividualNotice.${studentId}`] = deleteField();
          } else {
            updates[`IndividualNotice.${studentId}.notes.${noteKey}`] = deleteField();
          }
          const restIndividual = { ...(rawIndividual as Record<string, IndividualNoticeNotesV2>) };
          if (Object.keys(restNotes).length === 0) delete restIndividual[studentId];
          else restIndividual[studentId] = { ...bucket, notes: restNotes };
          updates.totalSize = this.estimateDocSize({ ...docData, IndividualNotice: restIndividual });
          await updateDoc(doc.ref, updates);
          found = true;
          break;
        }

        const docIndividualNotices = Array.isArray(rawIndividual) ? rawIndividual : [];
        const studentBucketIndex = docIndividualNotices.findIndex((b: any) => b.studentId === studentId);
        if (studentBucketIndex === -1) continue;

        const bucket = docIndividualNotices[studentBucketIndex];
        const notes = bucket.notes || [];
        const noteIndex = notes.findIndex((note: any) => this.getCreatedAtMillis(note?.createdAt) === timestamp);
        if (noteIndex === -1) continue;

        const updatedNotes = notes.filter((_: any, i: number) => i !== noteIndex);
        const updatedBuckets = [...docIndividualNotices];
        if (updatedNotes.length === 0) {
          updatedBuckets.splice(studentBucketIndex, 1);
        } else {
          updatedBuckets[studentBucketIndex] = { ...bucket, notes: updatedNotes };
        }
        await updateDoc(doc.ref, {
          IndividualNotice: updatedBuckets,
          totalSize: this.estimateDocSize({ ...docData, IndividualNotice: updatedBuckets, updatedAt: serverTimestamp() }),
          updatedAt: serverTimestamp(),
        } as any);
        found = true;
        break;
      }

      if (!found) throw new Error('삭제할 노트를 찾을 수 없습니다.');
      await this.rebuildNoticeNotesMember(userId, classId, studentId, memberGroup);
      this.invalidateNoticeNotesCache(userId, classId, memberGroup);
    } catch (error) {
      console.error('❌ 개별공지 노트 삭제 실패:', error);
      throw error;
    }
  }

  // 개별공지 노트 여러 학생의 노트를 한 번에 삭제 (Map<studentId, timestamps[]>)
  async deleteMultipleIndividualNoticeNotes(
    userId: string,
    classId: string,
    studentTimestampsMap: Map<string, number[]>,
    memberGroup?: string
  ): Promise<void> {
    try {
      if (studentTimestampsMap.size === 0) return;

      let totalTimestamps = 0;
      studentTimestampsMap.forEach(ts => { totalTimestamps += ts.length; });
      const snapshot = await this.getNoticeNoteDocsSnapshot(userId, classId, memberGroup);
      const studentTimestampsSetMap = new Map<string, Set<number>>();
      studentTimestampsMap.forEach((timestamps, studentId) => {
        studentTimestampsSetMap.set(studentId, new Set(timestamps));
      });
      let totalFoundCount = 0;

      for (const doc of snapshot.docs) {
        const docData = doc.data();
        const rawIndividual = docData?.['IndividualNotice'] ?? docData?.['개별공지노트'] ?? [];

        // V2: 객체인 경우 각 학생별 notes에서 해당 키만 deleteField
        if (rawIndividual && typeof rawIndividual === 'object' && !Array.isArray(rawIndividual)) {
          const indObj = rawIndividual as Record<string, IndividualNoticeNotesV2>;
          const updates: Record<string, any> = { updatedAt: serverTimestamp() };
          const restIndividual: Record<string, IndividualNoticeNotesV2> = { ...indObj };
          let hasChanges = false;

          for (const [sid, bucket] of Object.entries(indObj)) {
            const timestampsSet = studentTimestampsSetMap.get(sid);
            if (!timestampsSet?.size || !bucket?.notes || typeof bucket.notes !== 'object') continue;

            const notesObj = bucket.notes;
            const keysToDelete: string[] = [];
            for (const [noteKey, entry] of Object.entries(notesObj)) {
              const ms = this.getCreatedAtMillis(entry?.createdAt);
              if (ms != null && timestampsSet.has(ms)) {
                keysToDelete.push(noteKey);
                totalFoundCount++;
                timestampsSet.delete(ms);
              }
            }
            if (keysToDelete.length === 0) continue;

            hasChanges = true;
            const restNotes = { ...notesObj };
            for (const k of keysToDelete) {
              updates[`IndividualNotice.${sid}.notes.${k}`] = deleteField();
              delete restNotes[k];
            }
            if (Object.keys(restNotes).length === 0) {
              updates[`IndividualNotice.${sid}`] = deleteField();
              delete restIndividual[sid];
            } else {
              restIndividual[sid] = { ...bucket, notes: restNotes };
            }
          }
          if (hasChanges) {
            updates.totalSize = this.estimateDocSize({ ...docData, IndividualNotice: restIndividual });
            await updateDoc(doc.ref, updates);
          }
          continue;
        }

        const docIndividualNotices = Array.isArray(rawIndividual) ? rawIndividual : [];
        let hasChanges = false;
        const updatedBuckets = [...docIndividualNotices];

        for (let bucketIndex = updatedBuckets.length - 1; bucketIndex >= 0; bucketIndex--) {
          const bucket = updatedBuckets[bucketIndex];
          const studentId = bucket.studentId;
          const timestampsSet = studentTimestampsSetMap.get(studentId);
          if (!timestampsSet?.size) continue;

          const notes = bucket.notes || [];
          const indicesToDelete: number[] = [];
          for (let index = 0; index < notes.length; index++) {
            const note = notes[index];
            const noteTimestamp = this.getCreatedAtMillis(note?.createdAt);
            if (noteTimestamp != null && timestampsSet.has(noteTimestamp)) {
              indicesToDelete.push(index);
              totalFoundCount++;
            }
          }
          if (indicesToDelete.length === 0) continue;

          hasChanges = true;
          indicesToDelete.forEach(index => {
            const ms = this.getCreatedAtMillis(notes[index]?.createdAt);
            if (ms != null) timestampsSet.delete(ms);
          });
          const updatedNotes = notes.filter((_: any, i: number) => !indicesToDelete.includes(i));
          if (updatedNotes.length === 0) {
            updatedBuckets.splice(bucketIndex, 1);
          } else {
            updatedBuckets[bucketIndex] = { ...bucket, notes: updatedNotes };
          }
        }
        if (hasChanges) {
          await updateDoc(doc.ref, {
            IndividualNotice: updatedBuckets,
            totalSize: this.estimateDocSize({ ...docData, IndividualNotice: updatedBuckets, updatedAt: serverTimestamp() }),
            updatedAt: serverTimestamp(),
          } as any);
        }
      }

      if (totalFoundCount < totalTimestamps) {
        console.warn(`⚠️ 일부 노트를 찾을 수 없습니다. 요청: ${totalTimestamps}개, 찾음: ${totalFoundCount}개`);
      }
      const affectedStudentIds = Array.from(studentTimestampsMap.keys());
      for (const studentId of affectedStudentIds) {
        await this.rebuildNoticeNotesMember(userId, classId, studentId, memberGroup);
      }
      this.invalidateNoticeNotesCache(userId, classId, memberGroup);
    } catch (error) {
      console.error('❌ 개별공지 노트 여러 학생 삭제 실패:', error);
      throw error;
    }
  }

  // 개별공지 노트 여러 개 한 번에 삭제 (createdAt 타임스탬프 배열 기반, 학생 ID 포함)
  async deleteIndividualNoticeNotes(
    userId: string,
    classId: string,
    studentId: string,
    timestamps: number[],
    memberGroup?: string
  ): Promise<void> {
    try {
      if (timestamps.length === 0) return;

      const snapshot = await this.getNoticeNoteDocsSnapshot(userId, classId, memberGroup);
      const timestampsSet = new Set(timestamps);
      let foundCount = 0;

      for (const doc of snapshot.docs) {
        const docData = doc.data();
        const rawIndividual = docData?.['IndividualNotice'] ?? docData?.['개별공지노트'] ?? [];

        // V2: 객체인 경우 IndividualNotice[studentId].notes에서 해당 키들 deleteField
        if (rawIndividual && typeof rawIndividual === 'object' && !Array.isArray(rawIndividual)) {
          const bucket = (rawIndividual as Record<string, IndividualNoticeNotesV2>)[studentId];
          if (!bucket?.notes || typeof bucket.notes !== 'object') continue;

          const keysToDelete: string[] = [];
          for (const [noteKey, entry] of Object.entries(bucket.notes)) {
            const ms = this.getCreatedAtMillis(entry?.createdAt);
            if (ms != null && timestampsSet.has(ms)) {
              keysToDelete.push(noteKey);
              foundCount++;
              timestampsSet.delete(ms);
            }
          }
          if (keysToDelete.length === 0) continue;

          const restNotes = { ...bucket.notes };
          const updates: Record<string, any> = { updatedAt: serverTimestamp() };
          for (const k of keysToDelete) {
            updates[`IndividualNotice.${studentId}.notes.${k}`] = deleteField();
            delete restNotes[k];
          }
          const restIndividual = { ...(rawIndividual as Record<string, IndividualNoticeNotesV2>) };
          if (Object.keys(restNotes).length === 0) {
            updates[`IndividualNotice.${studentId}`] = deleteField();
            delete restIndividual[studentId];
          } else {
            restIndividual[studentId] = { ...bucket, notes: restNotes };
          }
          updates.totalSize = this.estimateDocSize({ ...docData, IndividualNotice: restIndividual });
          await updateDoc(doc.ref, updates);
          if (timestampsSet.size === 0) break;
          continue;
        }

        const docIndividualNotices = Array.isArray(rawIndividual) ? rawIndividual : [];
        const studentBucketIndex = docIndividualNotices.findIndex((b: any) => b.studentId === studentId);
        if (studentBucketIndex === -1) continue;

        const bucket = docIndividualNotices[studentBucketIndex];
        const notes = bucket.notes || [];
        const indicesToDelete: number[] = [];
        for (let index = 0; index < notes.length; index++) {
          const note = notes[index];
          const noteTimestamp = this.getCreatedAtMillis(note?.createdAt);
          if (noteTimestamp != null && timestampsSet.has(noteTimestamp)) {
            indicesToDelete.push(index);
            foundCount++;
            timestampsSet.delete(noteTimestamp);
          }
        }
        if (indicesToDelete.length === 0) continue;

        const updatedNotes = notes.filter((_: any, i: number) => !indicesToDelete.includes(i));
        const updatedBuckets = [...docIndividualNotices];
        if (updatedNotes.length === 0) {
          updatedBuckets.splice(studentBucketIndex, 1);
        } else {
          updatedBuckets[studentBucketIndex] = { ...bucket, notes: updatedNotes };
        }
        await updateDoc(doc.ref, {
          IndividualNotice: updatedBuckets,
          totalSize: this.estimateDocSize({ ...docData, IndividualNotice: updatedBuckets, updatedAt: serverTimestamp() }),
          updatedAt: serverTimestamp(),
        } as any);
        if (timestampsSet.size === 0) break;
      }
      
      console.log(`📊 삭제 결과: 요청 ${timestamps.length}개, 찾음 ${foundCount}개`);
      
      if (foundCount < timestamps.length) {
        console.warn(`⚠️ 일부 노트를 찾을 수 없습니다. 요청: ${timestamps.length}개, 찾음: ${foundCount}개`);
        console.warn(`⚠️ 찾지 못한 타임스탬프:`, Array.from(timestampsSet));
      }
      await this.rebuildNoticeNotesMember(userId, classId, studentId, memberGroup);
      this.invalidateNoticeNotesCache(userId, classId, memberGroup);
    } catch (error) {
      console.error('❌ 개별공지 노트 여러 개 삭제 실패:', error);
      throw error;
    }
  }

  // note-1 문서 초기화 (없으면 생성)
  private async ensureFirstNoteDoc(userId: string, classId: string): Promise<string> {
    try {
      await this.ensureNoteBookDoc(userId, classId);
      
      const note1Ref = this.getNoteDocRef(userId, classId, 'note-1');
      const note1Doc = await getDoc(note1Ref);
      
      if (!note1Doc.exists()) {
        await setDoc(note1Ref, {
          notes: [],
          totalSize: 0, // 용량 추적 필드 (bytes)
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } else {
        // 기존 문서에 totalSize 필드가 없으면 추가
        const data = note1Doc.data();
        if (data?.totalSize === undefined) {
          // 기존 notes 배열의 크기를 계산하여 초기화
          const currentSize = this.estimateDocSize(data);
          await updateDoc(note1Ref, {
            totalSize: currentSize
          });
        }
      }
      
      return 'note-1';
    } catch (error) {
      console.error('❌ note-1 문서 초기화 실패:', error);
      throw error;
    }
  }

  // 문서 크기 추정 (JSON 문자열 길이로 근사치 계산)
  private estimateDocSize(data: any): number {
    return JSON.stringify(data).length;
  }

  // 사용 가능한 note 문서 ID 찾기 (용량이 여유있는 것)
  private async findAvailableNoteDocId(
    userId: string,
    classId: string
  ): Promise<string> {
    try {
      // note-1부터 순차적으로 확인
      let noteNumber = 1;
      
      while (true) {
        const noteDocId = `note-${noteNumber}`;
        const noteDocRef = this.getNoteDocRef(userId, classId, noteDocId);
        const noteDoc = await getDoc(noteDocRef);
        
        if (!noteDoc.exists()) {
          // 문서가 없으면 생성
          await setDoc(noteDocRef, {
            notes: [],
            totalSize: 0, // 용량 추적 필드 (bytes)
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
          return noteDocId;
        }
        
        // 문서가 있으면 크기 확인
        const data = noteDoc.data();
        let currentSize: number;
        
        // totalSize 필드가 있으면 사용, 없으면 추정
        if (data?.totalSize !== undefined && typeof data.totalSize === 'number') {
          currentSize = data.totalSize;
        } else {
          // 기존 문서에 totalSize가 없으면 추정하고 업데이트
          currentSize = this.estimateDocSize(data);
          await updateDoc(noteDocRef, {
            totalSize: currentSize
          });
        }
        
        if (currentSize < MAX_NOTE_DOC_SIZE) {
          // 용량이 여유있으면 사용
          return noteDocId;
        }
        
        // 용량이 가득 차면 다음 번호로
        noteNumber++;
      }
    } catch (error) {
      console.error('❌ 사용 가능한 note 문서 찾기 실패:', error);
      throw error;
    }
  }

  // 카테고리 목록 조회 (NoteBook 문서의 categories 배열 필드에서 읽기, 캐시 사용)
  async getCategories(userId: string, classId: string): Promise<NotebookCategory[]> {
    try {
      await this.ensureNoteBookDoc(userId, classId);
      const { exists, data } = await this.getNoteBookDocCached(userId, classId);

      if (!exists || !data) {
        return [];
      }
      const categories = data?.categories || [];
      
      // Timestamp 변환 처리
      return categories.map((cat: any) => {
        let createdAt: Timestamp;
        
        if (cat.createdAt instanceof Timestamp) {
          createdAt = cat.createdAt;
        } else if (cat.createdAt?.seconds !== undefined) {
          createdAt = new Timestamp(cat.createdAt.seconds, cat.createdAt.nanoseconds || 0);
        } else if (cat.createdAt?.toDate) {
          createdAt = Timestamp.fromDate(cat.createdAt.toDate());
        } else {
          createdAt = Timestamp.now();
        }
        
        return {
          id: cat.id || '',
          name: cat.name || '',
          order: cat.order || 0,
          createdAt,
          locked: cat.locked === true,
          lockPasswordHash: typeof cat.lockPasswordHash === 'string' ? cat.lockPasswordHash : undefined
        } as NotebookCategory;
      }).sort((a: NotebookCategory, b: NotebookCategory) => a.order - b.order);
    } catch (error) {
      console.error('❌ 카테고리 조회 실패:', error);
      return [];
    }
  }

  /**
   * 기본 환경설정 저장: 로컬에서 편집한 순서·이름을 NoteBook.categories에 반영합니다.
   * - 기존 항목은 id로 매칭해 서버 레코드(잠금 해시 등)를 유지하고 name·order·locked만 갱신합니다.
   * - 임시 id(note_cat_*)는 같은 저장 흐름에서 방금 생성된 항목으로 보고, 이름으로 서버 항목과 1:1 매칭합니다.
   */
  async applyNoteBookCategoriesFromLocal(
    userId: string,
    classId: string,
    localOrdered: NotebookCategory[]
  ): Promise<void> {
    try {
      await this.ensureNoteBookDoc(userId, classId);
      this.invalidateNoteBookCache(userId, classId);
      const { exists, data } = await this.getNoteBookDocCached(userId, classId);
      if (!exists || !data) return;

      const noteBookDocRef = this.getNoteBookDocRef(userId, classId);
      const fromServer: any[] = [...(data.categories || [])];
      const byId = new Map(fromServer.map((c: any) => [c.id, { ...c }]));

      const result: any[] = [];
      const usedServerIds = new Set<string>();

      for (let index = 0; index < localOrdered.length; index++) {
        const lc = localOrdered[index];
        const trimmedName = (lc.name || '').trim();

        if (!lc.id.startsWith('note_cat_')) {
          const base = byId.get(lc.id);
          if (!base) continue;
          usedServerIds.add(lc.id);
          const next: any = {
            ...base,
            name: trimmedName,
            order: index,
            locked: !!lc.locked,
          };
          if (!lc.locked) delete next.lockPasswordHash;
          result.push(next);
        } else {
          const match = fromServer.find(
            (c: any) => !usedServerIds.has(c.id) && (c.name || '').trim() === trimmedName
          );
          if (!match) {
            console.warn('applyNoteBookCategoriesFromLocal: 새 카테고리 이름 매칭 실패', trimmedName);
            continue;
          }
          usedServerIds.add(match.id);
          const base = byId.get(match.id)!;
          const next: any = {
            ...base,
            name: trimmedName,
            order: index,
            locked: !!lc.locked,
          };
          if (!lc.locked) delete next.lockPasswordHash;
          result.push(next);
        }
      }

      await updateDoc(noteBookDocRef, {
        categories: result,
        updatedAt: serverTimestamp(),
      });

      this.invalidateNoteBookCache(userId, classId);
    } catch (error) {
      console.error('❌ 노트북 카테고리 이름·순서 반영 실패:', error);
      throw error;
    }
  }

  /**
   * 노트북 카테고리 정렬 순서 업데이트
   * - 이름 배열(orderedNames)의 순서를 기준으로 NoteBook.categories 배열을 재정렬
   * - 각 카테고리의 order 필드를 0부터 다시 매깁니다.
   * - 카테고리 이름은 기본 환경설정에서 중복을 막고 있으므로, name 기준 매핑이 안전합니다.
   */
  async updateCategoryOrderByNames(
    userId: string,
    classId: string,
    orderedNames: string[]
  ): Promise<void> {
    try {
      await this.ensureNoteBookDoc(userId, classId);
      const { exists, data } = await this.getNoteBookDocCached(userId, classId);
      if (!exists || !data) return;

      const noteBookDocRef = this.getNoteBookDocRef(userId, classId);
      const existingCategories: any[] = data.categories || [];

      // 이름 → 목표 index 매핑
      const nameToIndex = new Map<string, number>();
      orderedNames.forEach((name, index) => {
        nameToIndex.set(name, index);
      });

      // orderedNames에 있는 순서대로 정렬, 없는 것은 뒤로 보냄
      const sorted = [...existingCategories].sort((a, b) => {
        const aIndex = nameToIndex.has(a.name) ? nameToIndex.get(a.name)! : Number.MAX_SAFE_INTEGER;
        const bIndex = nameToIndex.has(b.name) ? nameToIndex.get(b.name)! : Number.MAX_SAFE_INTEGER;
        return aIndex - bIndex;
      });

      // order 필드 재계산
      const updatedCategories = sorted.map((cat, index) => ({
        ...cat,
        order: index,
      }));

      await updateDoc(noteBookDocRef, {
        categories: updatedCategories,
        updatedAt: serverTimestamp(),
      });

      this.invalidateNoteBookCache(userId, classId);
    } catch (error) {
      console.error('❌ 카테고리 순서 업데이트 실패:', error);
      throw error;
    }
  }

  /**
   * 노트북 카테고리 잠금/해제 토글 (즉시 반영용, 기본 환경설정에서는 저장 시 반영으로 대체 가능)
   */
  async updateCategoryLock(
    userId: string,
    classId: string,
    categoryId: string,
    locked: boolean
  ): Promise<void> {
    try {
      await this.ensureNoteBookDoc(userId, classId);
      const { exists, data } = await this.getNoteBookDocCached(userId, classId);
      if (!exists || !data) return;

      const noteBookDocRef = this.getNoteBookDocRef(userId, classId);
      const existingCategories: any[] = data.categories || [];
      const updatedCategories = existingCategories.map((cat: any) => {
        if (cat.id !== categoryId) return cat;
        const next = { ...cat, locked };
        if (!locked) delete next.lockPasswordHash;
        return next;
      });

      await updateDoc(noteBookDocRef, {
        categories: updatedCategories,
        updatedAt: serverTimestamp(),
      });
      this.invalidateNoteBookCache(userId, classId);
    } catch (error) {
      console.error('❌ 카테고리 잠금 업데이트 실패:', error);
      throw error;
    }
  }

  /**
   * 노트북 카테고리 잠금 상태 및 잠금 비밀번호 해시 일괄 반영 (기본 환경설정 저장 시 호출)
   * - updates: { categoryId, locked, lockPasswordHash? }[] (lockPasswordHash는 새로 설정할 때만 전달, 해제 시 빈 문자열 전달 시 필드 제거)
   */
  async setNoteBookCategoriesLockState(
    userId: string,
    classId: string,
    updates: Array<{ categoryId: string; locked: boolean; lockPasswordHash?: string | null }>
  ): Promise<void> {
    if (updates.length === 0) return;
    try {
      await this.ensureNoteBookDoc(userId, classId);
      const { exists, data } = await this.getNoteBookDocCached(userId, classId);
      if (!exists || !data) return;

      const noteBookDocRef = this.getNoteBookDocRef(userId, classId);
      const existingCategories: any[] = data.categories || [];
      const updateMap = new Map(updates.map(u => [u.categoryId, u]));
      const updatedCategories = existingCategories.map((cat: any) => {
        const u = updateMap.get(cat.id);
        if (!u) return cat;
        const next: any = { ...cat, locked: u.locked };
        if (!u.locked) {
          delete next.lockPasswordHash;
        } else if (u.lockPasswordHash != null && u.lockPasswordHash !== '') {
          next.lockPasswordHash = u.lockPasswordHash;
        }
        return next;
      });

      await updateDoc(noteBookDocRef, {
        categories: updatedCategories,
        updatedAt: serverTimestamp(),
      });
      this.invalidateNoteBookCache(userId, classId);
    } catch (error) {
      console.error('❌ 카테고리 잠금 상태 반영 실패:', error);
      throw error;
    }
  }

  // 카테고리 생성 (NoteBook 문서의 categories 배열에 추가). 쓰기 후 캐시 무효화.
  async createCategory(
    userId: string,
    classId: string,
    categoryName: string
  ): Promise<NotebookCategory> {
    try {
      await this.ensureNoteBookDoc(userId, classId);
      const { data } = await this.getNoteBookDocCached(userId, classId);
      const noteBookDocRef = this.getNoteBookDocRef(userId, classId);
      const existingCategories = data?.categories || [];

      const categoryId = `cat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newCategory: NotebookCategory = {
        id: categoryId,
        name: categoryName,
        order: existingCategories.length,
        createdAt: Timestamp.now(),
        locked: false
      };
      const updatedCategories = [...existingCategories, newCategory];

      await updateDoc(noteBookDocRef, {
        categories: updatedCategories,
        updatedAt: serverTimestamp()
      });
      this.invalidateNoteBookCache(userId, classId);
      return newCategory;
    } catch (error) {
      console.error('❌ 카테고리 생성 실패:', error);
      throw error;
    }
  }

  // 카테고리 삭제 (NoteBook 문서의 categories 배열에서 제거, 노트는 그대로 유지). 쓰기 후 캐시 무효화.
  async deleteNoteBookCategory(
    userId: string,
    classId: string,
    categoryId: string
  ): Promise<void> {
    try {
      await this.ensureNoteBookDoc(userId, classId);
      const { exists, data } = await this.getNoteBookDocCached(userId, classId);
      if (!exists || !data) return;

      const noteBookDocRef = this.getNoteBookDocRef(userId, classId);
      const existingCategories = data.categories || [];
      const updatedCategories = existingCategories.filter((cat: any) => cat.id !== categoryId);

      await updateDoc(noteBookDocRef, {
        categories: updatedCategories,
        updatedAt: serverTimestamp()
      });
      this.invalidateNoteBookCache(userId, classId);
    } catch (error) {
      console.error('❌ 카테고리 삭제 실패:', error);
      throw error;
    }
  }

  // 메모 저장
  async saveNote(
    userId: string,
    classId: string,
    note: Omit<NotebookNote, 'id' | 'createdAt' | 'updatedAt'>,
    categoryId?: string
  ): Promise<string> {
    try {
      // note-1 문서가 없으면 생성
      await this.ensureFirstNoteDoc(userId, classId);
      
      // 사용 가능한 note 문서 찾기
      const noteDocId = await this.findAvailableNoteDocId(userId, classId);
      const noteDocRef = this.getNoteDocRef(userId, classId, noteDocId);
      
      // 기존 카테고리별 노트 데이터 가져오기
      const noteDoc = await getDoc(noteDocRef);
      const existingData = noteDoc.data();
      const categorizedNotes = existingData?.categorizedNotes || {};

      // 새 노트 생성 (categoryId는 키로 사용하므로 노트에서 제거)
      const noteId = `note-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      const cleanNote: Omit<NotebookNote, 'categoryId' | 'category'> = {
        id: noteId,
        content: note.content || '',
        relatedStudents: note.relatedStudents || [],
        date: note.date || '',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      // 타임태그 정보 추가
      if (note.timeTagName) {
        cleanNote.timeTagName = note.timeTagName;
      }

      // 카테고리별로 노트 그룹화 — V2: { [noteId]: NotebookNote } 객체로 저장
      const targetCategoryId = categoryId || note.categoryId || '';
      const existingRaw = categorizedNotes[targetCategoryId];
      const existingArr = this.categoryNotesToArray(existingRaw);
      const categoryObj = this.categoryNotesToObject(existingArr);
      categoryObj[noteId] = cleanNote;
      categorizedNotes[targetCategoryId] = categoryObj;

      // 새 노트의 크기 계산
      const newNoteSize = JSON.stringify(cleanNote).length;
      const currentTotalSize = existingData?.totalSize || 0;

      // 전체 문서 크기 추정
      const updatedDocData = {
        categorizedNotes,
        totalSize: currentTotalSize + newNoteSize,
        createdAt: existingData?.createdAt,
        updatedAt: serverTimestamp()
      };
      
      // 실제 전체 문서 크기 계산
      const estimatedTotalSize = this.estimateDocSize(updatedDocData);
      
      // 전체 문서 업데이트 (categorizedNotes 포함)
      await updateDoc(noteDocRef, {
        categorizedNotes,
        totalSize: estimatedTotalSize,
        updatedAt: serverTimestamp()
      });

      // 캐시 무효화
      await this.invalidateCategoryCache(userId, classId, targetCategoryId);
      this.invalidateNotesListCache(userId, classId);

      return noteId;
    } catch (error) {
      console.error('❌ 메모 저장 실패:', error);
      throw error;
    }
  }

  // 메모 목록 조회 (캐시 사용; 쓰기 시 무효화)
  async getNotes(userId: string, classId: string, category?: string): Promise<NotebookNote[]> {
    try {
      const cacheKey = notesListCacheKey(userId, classId);
      const cached = this.__notesListCache.get(cacheKey);
      if (cached !== undefined) {
        if (category) return cached.filter(note => note.categoryId === category);
        return cached;
      }

      await this.ensureFirstNoteDoc(userId, classId);
      const notesCollectionRef = this.getNotesCollectionRef(userId, classId);
      const snapshot = await getDocs(notesCollectionRef);
      const allNotes: NotebookNote[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        const categorizedNotes = data.categorizedNotes || {};

        Object.entries(categorizedNotes).forEach(([categoryId, rawNotes]) => {
          const notes = this.categoryNotesToArray(rawNotes);
          notes.forEach((note: any) => {
            allNotes.push({
              id: note.id || '',
              content: note.content || '',
              categoryId: categoryId,
              relatedStudents: note.relatedStudents || [],
              date: note.date || '',
              timeTagId: undefined,
              timeTagName: note.timeTagName,
              fontSize: 16,
              createdAt: note.createdAt || Timestamp.now(),
              updatedAt: note.updatedAt || Timestamp.now()
            });
          });
        });
      });

      allNotes.sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
      this.__notesListCache.set(cacheKey, allNotes);

      if (category) return allNotes.filter(note => note.categoryId === category);
      return allNotes;
    } catch (error) {
      console.error('❌ 메모 조회 실패:', error);
      return [];
    }
  }

  // 메모 업데이트
  async updateNote(
    userId: string,
    classId: string,
    noteId: string,
    updates: Partial<Omit<NotebookNote, 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<void> {
    try {
      // 모든 note 문서에서 해당 노트 찾기
      const notesCollectionRef = this.getNotesCollectionRef(userId, classId);
      const snapshot = await getDocs(notesCollectionRef);

      for (const noteDoc of snapshot.docs) {
        const data = noteDoc.data();
        const categorizedNotes = { ...(data.categorizedNotes || {}) };

        for (const [categoryId, rawNotes] of Object.entries(categorizedNotes)) {
          const notes = this.categoryNotesToArray(rawNotes);
          const noteIndex = notes.findIndex((n: any) => n.id === noteId);

          if (noteIndex !== -1) {
            const cleanUpdates: any = {};
            Object.keys(updates).forEach((key) => {
              if (key === 'fontSize' || key === 'timeTagId') return;
              const value = (updates as any)[key];
              if (value !== undefined) cleanUpdates[key] = value;
            });

            notes[noteIndex] = {
              ...notes[noteIndex],
              ...cleanUpdates,
              updatedAt: Timestamp.now()
            };
            categorizedNotes[categoryId] = this.categoryNotesToObject(notes);

            const estimatedTotalSize = this.estimateDocSize({
              ...data,
              categorizedNotes,
              updatedAt: serverTimestamp()
            });
            const noteDocRef = doc(notesCollectionRef, noteDoc.id);
            await updateDoc(noteDocRef, {
              categorizedNotes,
              totalSize: estimatedTotalSize,
              updatedAt: serverTimestamp()
            });

            await this.invalidateCategoryCache(userId, classId, categoryId);
            this.invalidateNotesListCache(userId, classId);
            console.log('✅ 노트 업데이트 완료:', { noteId, categoryId, docId: noteDoc.id });
            return;
          }
        }
      }

      throw new Error(`노트를 찾을 수 없습니다: ${noteId}`);
    } catch (error) {
      console.error('❌ 메모 업데이트 실패:', error);
      throw error;
    }
  }

  // 메모 삭제
  async deleteNote(userId: string, classId: string, noteId: string): Promise<void> {
    try {
      // 모든 note 문서에서 해당 노트 찾기
      const notesCollectionRef = this.getNotesCollectionRef(userId, classId);
      const snapshot = await getDocs(notesCollectionRef);

      for (const noteDoc of snapshot.docs) {
        const data = noteDoc.data();
        const categorizedNotes = { ...(data.categorizedNotes || {}) };

        for (const [categoryId, rawNotes] of Object.entries(categorizedNotes)) {
          const notes = this.categoryNotesToArray(rawNotes);
          if (notes.findIndex((n: any) => n.id === noteId) === -1) continue;

          const updatedNotes = notes.filter((n: any) => n.id !== noteId);
          if (updatedNotes.length > 0) {
            categorizedNotes[categoryId] = this.categoryNotesToObject(updatedNotes);
          } else {
            delete categorizedNotes[categoryId];
          }

          const estimatedTotalSize = this.estimateDocSize({
            ...data,
            categorizedNotes,
            updatedAt: serverTimestamp()
          });
          const noteDocRef = doc(notesCollectionRef, noteDoc.id);
          await updateDoc(noteDocRef, {
            categorizedNotes,
            totalSize: estimatedTotalSize,
            updatedAt: serverTimestamp()
          });

          await this.invalidateCategoryCache(userId, classId, categoryId);
          this.invalidateNotesListCache(userId, classId);
          console.log('✅ 노트 삭제 완료:', { noteId, categoryId, docId: noteDoc.id });
          return;
        }
      }

      throw new Error(`노트를 찾을 수 없습니다: ${noteId}`);
    } catch (error) {
      console.error('❌ 메모 삭제 실패:', error);
      throw error;
    }
  }

  // 카테고리 삭제 (해당 카테고리의 모든 노트 삭제)
  async deleteCategoryWithNotes(userId: string, classId: string, categoryId: string): Promise<void> {
    try {
      console.log('🗑️ 카테고리 삭제 시작:', { userId, classId, categoryId });

      // 모든 note 문서에서 해당 카테고리의 노트들을 삭제
      const notesCollectionRef = this.getNotesCollectionRef(userId, classId);
      const snapshot = await getDocs(notesCollectionRef);

      for (const noteDoc of snapshot.docs) {
        const data = noteDoc.data();
        const categorizedNotes = data.categorizedNotes || {};

        // 해당 카테고리가 존재하는지 확인
        if (categorizedNotes[categoryId]) {
          // 카테고리 삭제
          delete categorizedNotes[categoryId];

          // 전체 문서 크기 재계산
          const updatedDocData = {
            categorizedNotes,
            totalSize: 0, // 임시값, 아래에서 계산
            createdAt: data?.createdAt,
            updatedAt: serverTimestamp()
          };
          const estimatedTotalSize = this.estimateDocSize(updatedDocData);

          const noteDocRef = doc(notesCollectionRef, noteDoc.id);
          await updateDoc(noteDocRef, {
            categorizedNotes,
            totalSize: estimatedTotalSize,
            updatedAt: serverTimestamp()
          });

          // NoteBook 문서의 categories 배열에서도 카테고리 삭제
          await this.deleteNoteBookCategory(userId, classId, categoryId);

          // 캐시 문서 삭제
          await this.deleteCategoryCache(userId, classId, categoryId);
          this.invalidateNotesListCache(userId, classId);

          console.log('✅ 카테고리 삭제 완료:', { categoryId, docId: noteDoc.id });
          return;
        }
      }

      console.log('⚠️ 삭제할 카테고리를 찾지 못함:', categoryId);
    } catch (error) {
      console.error('❌ 카테고리 삭제 실패:', error);
      throw error;
    }
  }

  // 카테고리 삭제 시 캐시 문서도 삭제
  private async deleteCategoryCache(userId: string, classId: string, categoryId: string): Promise<void> {
    try {
      const cacheDocRef = this.getCacheDocRef(userId, classId, categoryId);
      await deleteDoc(cacheDocRef);
      console.log(`🗑️ ${categoryId} 카테고리 캐시 문서 삭제`);
    } catch (error) {
      // 캐시 문서가 없는 경우 무시
      console.log(`ℹ️ ${categoryId} 카테고리 캐시 문서가 존재하지 않아 삭제 생략`);
    }
  }

  // 마이그레이션 함수: 기존 notes 배열을 categorizedNotes 맵으로 변환
  async migrateNotesToCategorizedNotes(userId: string, classId: string): Promise<void> {
    try {
      console.log('🔄 노트 마이그레이션 시작:', { userId, classId });

      const notesCollectionRef = this.getNotesCollectionRef(userId, classId);
      const snapshot = await getDocs(notesCollectionRef);

      let migratedCount = 0;

      for (const noteDoc of snapshot.docs) {
        const data = noteDoc.data();
        const existingNotes = data.notes || [];
        const categorizedNotes = data.categorizedNotes || {};

        // 이미 마이그레이션된 문서인지 확인
        if (existingNotes.length > 0 && Object.keys(categorizedNotes).length === 0) {
          console.log(`📄 문서 ${noteDoc.id} 마이그레이션 중...`);

          // 기존 notes를 카테고리별로 그룹화
          existingNotes.forEach((note: any) => {
            const categoryId = note.categoryId || note.category || ''; // 이전 버전 호환성
            if (!categorizedNotes[categoryId]) {
              categorizedNotes[categoryId] = [];
            }

            // note에서 category 필드 제거하고 categoryId 필드도 제거 (키로 사용하므로)
            const migratedNote = { ...note };
            delete migratedNote.category; // 기존 category 필드 제거
            delete migratedNote.categoryId; // categoryId 필드 제거 (키로 사용하므로)

            categorizedNotes[categoryId].push(migratedNote);
          });

          const noteDocRef = doc(notesCollectionRef, noteDoc.id);
          await updateDoc(noteDocRef, {
            categorizedNotes,
            updatedAt: serverTimestamp()
            // notes 배열은 유지 (롤백 가능성 위해)
          });

          migratedCount++;
          console.log(`✅ 문서 ${noteDoc.id} 마이그레이션 완료: ${existingNotes.length}개 노트`);
        }
      }

      console.log(`🎉 마이그레이션 완료: ${migratedCount}개 문서 처리됨`);
    } catch (error) {
      console.error('❌ 노트 마이그레이션 실패:', error);
      throw error;
    }
  }

  // 캐시 문서 경로 반환 (ClassBoard/NoteBook 아래 cache 서브컬렉션)
  private getCacheDocRef(userId: string, classId: string, categoryId: string) {
    const noteBookRef = this.getNoteBookDocRef(userId, classId);
    return doc(collection(noteBookRef, 'cache'), categoryId);
  }

  // 카테고리별 캐시에서 최신 노트 로드 (최대 10개)
  async getCachedRecentNotes(userId: string, classId: string, categoryId: string): Promise<NotebookNote[]> {
    try {
      const cacheDocRef = this.getCacheDocRef(userId, classId, categoryId);
      const cacheDoc = await getDoc(cacheDocRef);

      if (cacheDoc.exists()) {
        const cacheData = cacheDoc.data() as CategoryCache;
        console.log(`📋 캐시에서 ${categoryId} 카테고리 노트 로드: ${cacheData.notes.length}개`);
        return cacheData.notes;
      }

      return [];
    } catch (error) {
      console.error('❌ 캐시 노트 로드 실패:', error);
      return [];
    }
  }

  /** Firestore는 undefined 불가 — 객체에서 undefined 제거 */
  private stripUndefined<T extends Record<string, any>>(obj: T): Record<string, any> {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(obj)) {
      if (v === undefined) continue;
      if (v !== null && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date) && typeof (v as any).toDate !== 'function') {
        out[k] = this.stripUndefined(v as Record<string, any>);
      } else if (Array.isArray(v)) {
        out[k] = v.map(item => item !== null && typeof item === 'object' && !Array.isArray(item) && typeof (item as any).toDate !== 'function'
          ? this.stripUndefined(item as Record<string, any>)
          : item);
      } else {
        out[k] = v;
      }
    }
    return out;
  }

  // 카테고리별 캐시 업데이트
  async updateCategoryCache(userId: string, classId: string, categoryId: string): Promise<void> {
    try {
      const allNotes = await this.getNotes(userId, classId, categoryId);
      const recentNotes = allNotes.slice(0, 10);

      const cacheData = {
        notes: recentNotes,
        lastUpdated: Timestamp.now(),
        categoryId: categoryId
      };
      const sanitized = this.stripUndefined(cacheData);

      const cacheDocRef = this.getCacheDocRef(userId, classId, categoryId);
      await setDoc(cacheDocRef, sanitized);

      console.log(`✅ ${categoryId} 카테고리 캐시 업데이트: ${recentNotes.length}개 노트`);
    } catch (error) {
      console.error('❌ 카테고리 캐시 업데이트 실패:', error);
      throw error;
    }
  }

  // 캐시 무효화 (노트 저장/수정/삭제 시 호출)
  async invalidateCategoryCache(userId: string, classId: string, categoryId: string): Promise<void> {
    try {
      const cacheDocRef = this.getCacheDocRef(userId, classId, categoryId);
      await updateDoc(cacheDocRef, {
        lastUpdated: Timestamp.now(),
        notes: [] // 캐시 비우기
      });
      console.log(`🗑️ ${categoryId} 카테고리 캐시 무효화`);
    } catch (error) {
      // 캐시 문서가 없는 경우 무시
      console.log(`ℹ️ ${categoryId} 카테고리 캐시가 존재하지 않아 무효화 생략`);
    }
  }

  // 카테고리 클릭 시 최신 노트 로드 (캐시 우선 사용)
  async loadRecentNotesForCategory(userId: string, classId: string, categoryId: string): Promise<NotebookNote[]> {
    try {
      // 먼저 캐시에서 시도
      const cachedNotes = await this.getCachedRecentNotes(userId, classId, categoryId);

      if (cachedNotes.length > 0) {
        // 캐시가 있고 노트가 있으면 캐시 반환
        console.log(`⚡ ${categoryId} 카테고리: 캐시에서 ${cachedNotes.length}개 노트 로드`);
        return cachedNotes;
      }

      // 캐시가 없거나 비어있으면 서버에서 로드 후 캐시 업데이트
      console.log(`🔄 ${categoryId} 카테고리: 서버에서 노트 로드 및 캐시 업데이트`);
      const allNotes = await this.getNotes(userId, classId, categoryId);
      const recentNotes = allNotes.slice(0, 10);

      // 백그라운드에서 캐시 업데이트
      this.updateCategoryCache(userId, classId, categoryId).catch(error => {
        console.error('백그라운드 캐시 업데이트 실패:', error);
      });

      return recentNotes;
    } catch (error) {
      console.error('❌ 카테고리 노트 로드 실패:', error);
      return [];
    }
  }
}

export const boardService = new BoardService();
